/**
 * Legal Document Creator Global Tool
 *
 * Creates professional legal documents from templates using guided input collection.
 * Supports Employment Agreement, NDA, and Independent Contractor Agreement.
 * Generates PDF and DOCX output in English or Spanish.
 */

import { getAuth } from 'firebase/auth';
import { knownAccountHint } from '../../accountHint';
import { SERVER_METER_HEADERS } from '../../proxyMeter';
import { proxyAuthHeaders } from '../../proxyAuth';
import {
} from '../../../config/modelTiers.generated';
import {
  CLAUDE_FORMATTER_MODEL,
  CLAUDE_FORMATTER_DISPLAY,
  TESTING_MODE,
} from '../../../config/agenticWorkflowConfig';
import { isAgenticWorkflowsAuthorized } from '../../agenticWorkflows';
import {
  generateWorkflowActionButtonsHtml,
  isCancelCommand,
  formatResearchToHtml,
  PREST0_PRIMARY_CTA_BG,
  PREST0_SECONDARY_CTA_BG,
} from '../../agenticWorkflows/helpers';
import { BUTTON_IDS as TRANSLATE_BUTTON_IDS } from './translate';
import { storeWorkflowOutput } from '../workflowOutputStore';
import { buildModelDisplay } from '../modelDisplay';
import { serializeFile } from '../../../utils/documentGenerator';
import { buildLegalDocPdf } from '../../../utils/legalDocPdf';
import { buildLegalDocDocx } from '../../../utils/legalDocDocx';
import { fillI589Form, fieldsToI589Data } from '../../../utils/i589FormFiller';
import { buildI589DocxWorksheet } from '../../../utils/i589DocxWorksheet';
import { readDocuments, hasAnalyzableFiles } from '../documentReader';
import { findAffirmativeLegalDocDraftIntent } from '../manifestIntentTables';
import { getTemplateById, LEGAL_TEMPLATES } from '../../../data/legalTemplates';
import type { LegalTemplate } from '../../../data/legalTemplates';
import {
  getTemplateByIdAnyScope,
  loadAllAvailableTemplates,
  saveReusableTemplate,
  type SaveTemplateInput,
} from '../../../data/legalTemplates/registry';
import { hasNegationAround } from '../triggerHelpers';
import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  ToolTheme,
  TokenUsageMetadata,
  SerializedAttachment,
} from '../types';

// ============================================================================
// Proxy URL
// ============================================================================

function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// ============================================================================
// Model Display Name Helper — single consolidated builder, imported in the
// top import block (2026-07-06: the local copy here had drifted —
// raw-vendor-id fallthrough, unversioned Opus pinned to Apex-4.7 instead of
// 4.8, no Echo/Nova-5 branches).
// ============================================================================

// ============================================================================
// SSE Stream Reader
// ============================================================================

interface StreamCallbacks {
  onChunk?: (chunk: string, accumulated: string) => void;
  onComplete?: (fullText: string, usage: TokenUsageMetadata | undefined) => void;
}

/**
 * Result of reading a streaming SSE response. Exposes the final stop/finish
 * reason so callers can trigger continuation when output was truncated at the
 * max_tokens ceiling.
 */
interface StreamReadResult {
  text: string;
  stopReason?: string;       // Anthropic: stop_reason
  finishReason?: string;     // OpenAI/OpenRouter: finish_reason
  usage: TokenUsageMetadata | undefined;
  providerError?: {
    message: string;
    status?: number;
    requestId?: string;
    openrouterId?: string;
    raw?: string;
  };
}

/**
 * Structured error thrown when the SSE stream terminates abnormally —
 * either via an explicit `event: error` frame or with a recognizable
 * transient signal (ECONNRESET, stream closed before [DONE], 502/503/524).
 */
class StreamGenerationError extends Error {
  transient: boolean;
  status?: number;
  requestId?: string;
  openrouterId?: string;
  model?: string;
  alreadyStreamedChars: number;

  constructor(params: {
    message: string;
    transient: boolean;
    status?: number;
    requestId?: string;
    openrouterId?: string;
    model?: string;
    alreadyStreamedChars: number;
  }) {
    super(params.message);
    this.name = 'StreamGenerationError';
    this.transient = params.transient;
    this.status = params.status;
    this.requestId = params.requestId;
    this.openrouterId = params.openrouterId;
    this.model = params.model;
    this.alreadyStreamedChars = params.alreadyStreamedChars;
  }
}

function isTransientHttpStatus(status?: number): boolean {
  if (!status) return false;
  return status === 408 || status === 425 || status === 429 ||
    status === 502 || status === 503 || status === 504 || status === 524;
}

async function readStreamedText(
  response: Response,
  callbacks?: StreamCallbacks
): Promise<StreamReadResult> {
  const reader = response.body!.getReader();
  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let model: string | undefined;
  let stopReason: string | undefined;
  let finishReason: string | undefined;
  let sawDone = false;
  let currentEvent: string | undefined;
  let providerError: StreamReadResult['providerError'];
  let requestId: string | undefined =
    response.headers.get('x-request-id') ||
    response.headers.get('x-openrouter-id') ||
    undefined;

  const handleParsedPayload = (parsed: Record<string, unknown>) => {
    // --- Anthropic SSE format ---
    if (
      parsed.type === 'content_block_delta' &&
      parsed.delta &&
      (parsed.delta as Record<string, unknown>).type === 'text_delta'
    ) {
      const chunk = ((parsed.delta as Record<string, unknown>).text as string) || '';
      accumulated += chunk;
      callbacks?.onChunk?.(chunk, accumulated);
    } else if (parsed.type === 'message_start' && parsed.message) {
      const msg = parsed.message as Record<string, unknown>;
      if (msg.usage) {
        const usage = msg.usage as Record<string, unknown>;
        inputTokens = (usage.input_tokens as number) || undefined;
      }
      if (msg.model) {
        model = msg.model as string;
      }
      if (msg.id && !requestId) {
        requestId = String(msg.id);
      }
    } else if (parsed.type === 'message_delta') {
      if (parsed.usage) {
        const usage = parsed.usage as Record<string, unknown>;
        outputTokens = (usage.output_tokens as number) || outputTokens;
      }
      if (parsed.delta && typeof parsed.delta === 'object') {
        const d = parsed.delta as Record<string, unknown>;
        if (typeof d.stop_reason === 'string') stopReason = d.stop_reason;
      }
    } else if (parsed.type === 'error') {
      const err = (parsed.error as Record<string, unknown>) || {};
      providerError = {
        message: String(err.message || 'Anthropic stream error'),
        status: (err.status as number | undefined),
        requestId,
        raw: JSON.stringify(parsed).slice(0, 500),
      };
    }

    // --- OpenAI / OpenRouter SSE format ---
    else if (parsed.choices && Array.isArray(parsed.choices)) {
      const choice = (parsed.choices as Record<string, unknown>[])[0];
      if (choice?.delta) {
        const delta = choice.delta as Record<string, unknown>;
        const content = (delta.content as string) || '';
        if (content) {
          accumulated += content;
          callbacks?.onChunk?.(content, accumulated);
        }
      }
      if (choice && typeof choice.finish_reason === 'string') {
        finishReason = choice.finish_reason as string;
      }
      if (parsed.model && !model) {
        model = parsed.model as string;
      }
      if (parsed.id && !requestId) {
        requestId = String(parsed.id);
      }
      if (parsed.usage) {
        const usage = parsed.usage as Record<string, unknown>;
        inputTokens = (usage.prompt_tokens as number) || inputTokens;
        outputTokens = (usage.completion_tokens as number) || outputTokens;
      }
    }

    // --- OpenRouter-level error envelope ---
    else if (parsed.error && typeof parsed.error === 'object') {
      const err = parsed.error as Record<string, unknown>;
      providerError = {
        message: String(err.message || 'Provider stream error'),
        status: (err.code as number | undefined) ?? (err.status as number | undefined),
        requestId,
        raw: JSON.stringify(parsed).slice(0, 500),
      };
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const rawLine of lines) {
        const line = rawLine.replace(/\r$/, '');
        const trimmed = line.trim();
        if (!trimmed) {
          // blank line terminates an SSE event
          currentEvent = undefined;
          continue;
        }
        if (trimmed.startsWith('event: ')) {
          currentEvent = trimmed.slice(7).trim();
          continue;
        }
        if (trimmed === 'data: [DONE]') {
          sawDone = true;
          continue;
        }
        if (!trimmed.startsWith('data: ')) continue;
        const payload = trimmed.slice(6);

        // Explicit SSE error frame (`event: error\ndata: {...}`)
        if (currentEvent === 'error') {
          try {
            const parsed = JSON.parse(payload) as Record<string, unknown>;
            const err = (parsed.error as Record<string, unknown>) || parsed;
            providerError = {
              message: String((err as any).message || payload.slice(0, 200)),
              status: (err as any).status ?? (err as any).code,
              requestId,
              raw: payload.slice(0, 500),
            };
          } catch {
            providerError = {
              message: payload.slice(0, 200),
              requestId,
              raw: payload.slice(0, 500),
            };
          }
          continue;
        }

        try {
          const parsed = JSON.parse(payload) as Record<string, unknown>;
          handleParsedPayload(parsed);
        } catch {
          // ignore malformed SSE lines
        }
      }
    }
  } catch (readErr: any) {
    const message = readErr?.message || String(readErr);
    const transient =
      /ECONNRESET|socket hang up|network error|aborted|closed before/i.test(message) ||
      readErr?.code === 'ECONNRESET';
    throw new StreamGenerationError({
      message: `Stream read error: ${message}`,
      transient,
      requestId,
      model,
      alreadyStreamedChars: accumulated.length,
    });
  }

  // Explicit provider error frame
  if (providerError) {
    throw new StreamGenerationError({
      message: `Provider stream error: ${providerError.message}`,
      transient: isTransientHttpStatus(providerError.status),
      status: providerError.status,
      requestId: providerError.requestId || requestId,
      model,
      alreadyStreamedChars: accumulated.length,
    });
  }

  // Stream ended without [DONE] marker — treat as transient if no content.
  if (!sawDone && accumulated.length === 0) {
    throw new StreamGenerationError({
      message: 'Stream ended before any content was received (no [DONE] marker).',
      transient: true,
      requestId,
      model,
      alreadyStreamedChars: 0,
    });
  }

  const tokenUsage: TokenUsageMetadata | undefined =
    inputTokens !== undefined || outputTokens !== undefined
      ? { inputTokens, outputTokens, model }
      : undefined;

  callbacks?.onComplete?.(accumulated, tokenUsage);
  return {
    text: accumulated,
    stopReason,
    finishReason,
    usage: tokenUsage,
    providerError,
  };
}

// ============================================================================
// Tool Theme + Colors
// ============================================================================

const LEGAL_DOC_THEME: ToolTheme = {
  accent: '#1e3a5f',
  accentBg: '#e8f0fe',
  border: '#3b6fa0',
  subtitle: '#475569',
  muted: '#64748b',
};

// ============================================================================
// Escape-hatch Feature Flag (FH-ESCAPE-006)
//
// When ON, every "information needed" prompt in the legal-doc workflow
// appends a short friendly line telling the user they can type "cancel"
// to exit the workflow. This pairs with the text-only break-out patterns
// in `hooks/awaitingInputBreakOut.ts` so saying "cancel" at any time
// reliably exits the workflow without needing the stop button.
//
// Default: ON. Centralized here so we can flip via env var / tenant
// config in a single place without touching per-message copy.
// ============================================================================

const SHOW_LEGAL_DOC_ESCAPE_HATCH: boolean = (() => {
  const raw = (process.env.REACT_APP_LEGAL_DOC_ESCAPE_HATCH || '').trim().toLowerCase();
  if (raw === 'false' || raw === '0' || raw === 'off' || raw === 'no') return false;
  return true;
})();

// ============================================================================
// Button IDs
// ============================================================================

export const BUTTON_IDS = {
  createDocument: 'global-tool-legal-doc-creator-btn',
  employmentAgreement: 'legal-doc-employment-btn',
  nda: 'legal-doc-nda-btn',
  contractorAgreement: 'legal-doc-contractor-btn',
  qmeAdvocacyMemo: 'legal-doc-qme-memo-btn',
  wcabPreHearingStatement: 'legal-doc-wcab-phs-btn',
  immigrationPreHearingStatement: 'legal-doc-immigration-phs-btn',
  i589Asylum: 'legal-doc-i589-asylum-btn',
  // 9th picker option — freeform drafter (Miles 2026-05-08)
  generalLegalDocument: 'legal-doc-general-btn',
  // Post-generation action: save current output as a reusable template
  saveAsTemplate: 'legal-doc-save-as-template-btn',
};

// ============================================================================
// Tool State
// ============================================================================

export interface LegalDocCreatorState {
  selectedTemplate: string | null;
  collectedFields: Record<string, string>;
  documentLanguage: 'en' | 'es';
  isGenerating: boolean;
  generatedContent?: string;
  originalPacketFileCount?: number;
  /**
   * Inferred conventional filename from the drafter's first '#'-level
   * heading on the freeform path. Used as the default title in the
   * Save-as-Reusable-Template flow (Layer D).
   */
  generatedTitle?: string;
  /**
   * When the Save-as-Template flow is in progress, this holds the
   * partially-filled intake so we can resume after awaiting-input turns.
   */
  savePending?: {
    stage: 'awaiting-title' | 'awaiting-description' | 'awaiting-scope' | 'awaiting-attorney-review' | 'ready';
    title?: string;
    description?: string;
    scope?: 'user' | 'tenant' | 'global-candidate';
    requiresAttorneyReview?: boolean;
  };
}

// ============================================================================
// Trigger Pattern
// ============================================================================

/**
 * Keyword-level document-type trigger. Matches any mention of a legal
 * document type we support — e.g. "pre-hearing statement", "NDA",
 * "demand letter", "I-589", etc.
 *
 * IMPORTANT (FH-ESCAPE-006, 2026-05-07): a keyword match alone is NOT
 * sufficient to fire the drafter. `matchLegalDocTrigger` additionally
 * requires an affirmative draft verb near the keyword (unless the message
 * is a button-ID payload), AND must not look like meta-feedback ("the PHS
 * tool created a new doc when I didn't ask") or a read/learn-about request
 * ("please read the prehearing statement", "what is a prehearing
 * statement"). Those cases now fall through so the orchestrator can route
 * them to document-analyzer, deep-research, or a conversational reply.
 */
/**
 * Generic legal-document noun list, shared by the trigger pattern, the
 * affirmative-intent matcher, and `detectTemplateIdFromText`'s Layer C
 * fallback. Covers the everyday document types the freeform general-legal-
 * document drafter (PR #67) is designed to produce — motions, complaints,
 * declarations, letters, notices, etc. — that aren't one of the 8 canonical
 * static templates.
 *
 * Kept as a string so it can be inlined into multiple regex literals
 * without accidental drift.
 */
const GENERAL_DOC_NOUN_SOURCE =
  '(?:motion|brief|memorandum|memo|letter|notice|declaration|affidavit|subpoena|stipulation|petition|pleading|filing|document|order|response|opposition|reply|objection|application|request|complaint|answer|discovery|interrogator(?:y|ies)|waiver|consent|release|agreement|carta|memor[áa]ndum|memorando|declaraci[oó]n|petici[oó]n|moci[oó]n|aviso|estipulaci[oó]n|solicitud|respuesta|oposici[oó]n|objeci[oó]n|documento|queja|reclamo)';

/**
 * Noun-phrase-with-recipient pattern for general legal documents.
 * Examples that should match (Miles 2026-05-09 regression — the new
 * general-document drafter must accept these even without a leading
 * draft verb):
 *   - "a formal complaint to my neighbor"
 *   - "a complaint to my neighbor"
 *   - "a strongly-worded letter to the landlord"
 *   - "a notice of breach to ABC Corp"
 *   - "a motion to continue trial"  (recipient = the action target)
 *   - "a declaration in support of my U-visa"
 *   - "una queja formal a mi vecino"
 *
 * The recipient/target slot is intentionally permissive — we accept any
 * noun phrase after "to/for/against/in support of/in opposition to" so
 * the heuristic captures "to my neighbor", "for the court", "against ABC
 * Corp", etc. Keeping it permissive matches user intent: an explicit
 * recipient/target signals "draft this for me", not "explain what this
 * is". The read/meta guards still run first and gate informational
 * phrasings out.
 */
const GENERAL_DOC_NOUN_PHRASE_WITH_RECIPIENT_SOURCE =
  // English: a/an/the/this/that/my/our/some/another
  // Spanish: un/una/unos/unas/el/la/los/las/este/esta/mi/nuestro/algun(a)
  '\\b(?:a|an|the|this|that|my|our|some|another|un|una|unos|unas|el|la|los|las|este|esta|estos|estas|mi|nuestr[oa]|alg[uú]n[oa]?)\\s+' +
  // Up to 3 modifier/connector words between determiner and the doc
  // noun. This covers "a strongly-worded letter", "a notice of breach",
  // "una queja formal", "a brief memo", etc.
  '(?:[a-zA-Záéíóúñ-]+\\s+){0,3}' +
  GENERAL_DOC_NOUN_SOURCE +
  // Up to 3 trailing modifier words between the doc noun and the
  // recipient connector. This covers "notice of breach to ABC", "motion
  // for summary judgment to the court", "queja formal a mi vecino",
  // "declaration in support of my U-visa".
  '(?:\\s+[a-zA-Záéíóúñ-]+){0,3}' +
  '\\s+(?:to|for|against|in\\s+support\\s+of|in\\s+opposition\\s+to|al?|a\\s+la|para|contra|en\\s+apoyo\\s+de|en\\s+contra\\s+de)\\s+\\S+';

const LEGAL_DOC_TRIGGER_PATTERN =
  new RegExp(
    '\\b(legal\\s+doc(ument)?|' +
      'create\\s+(a\\s+)?(contract|agreement|nda|letter|demand)|' +
      'draft\\s+(a\\s+)?(contract|agreement|nda|employment|contractor|letter|demand|statement)|' +
      'make\\s+(a\\s+)?(contract|nda|agreement|letter|demand|statement)|' +
      'prepare\\s+(a\\s+)?(legal\\s+)?(document|statement|application)|' +
      'employment\\s+agreement|nda|non[-\\s]?disclosure|contractor\\s+agreement|' +
      'demand\\s+letter|cease\\s+and\\s+desist|carta\\s+de\\s+demanda|' +
      'necesito\\s+(un\\s+)?(contrato|acuerdo|carta)|' +
      'crear\\s+(un\\s+)?(documento|contrato|carta)|' +
      'pre[-\\s]?hearing\\s+statement|prehearing\\s+statement|rule\\s+10563|\\bphs\\b|' +
      'wcab.*pre[-\\s]?hearing|declaraci[oó]n\\s+previa|' +
      'qme|advocacy[\\s.-]?memo|qme[\\s.-]?memo|' +
      'workers[\\s.-]?comp.*memo|workers[\\s.-]?compensation.*memo|' +
      'memo\\s+medico|memorando\\s+qme|' +
      'i[-\\s]?589|asylum\\s+application|asilo|solicitud\\s+de\\s+asilo|' +
      // PR #67 / Miles 2026-05-09: general-legal-document drafter triggers.
      // Drafting verb + generic doc noun (covers "draft a motion",
      // "write a complaint", "prepare a declaration", etc.).
      '(?:create|draft|prepare|generate|write|make|start|begin|put\\s+together|build|file|redact|redactar|crear|preparar|escribir|generar|hacer|presentar)\\s+(?:me\\s+)?(?:a|an|the|another|one|my|our|un|una|otro|otra|el|la)?\\s*(?:[a-zA-Záéíóúñ-]+\\s+){0,3}' +
      GENERAL_DOC_NOUN_SOURCE +
      '|' +
      // Noun-phrase + recipient ("a formal complaint to my neighbor")
      // — no draft verb required when an explicit recipient/target slot
      // is present.
      GENERAL_DOC_NOUN_PHRASE_WITH_RECIPIENT_SOURCE +
      ')',
    'i'
  );

/**
 * Read / learn-about / meta patterns that must NOT fire the drafter even
 * when a legal-doc keyword is also present. If any of these match AND no
 * affirmative draft verb is near the keyword, the tool declines the match.
 */
const LEGAL_DOC_READ_OR_META_PATTERNS: RegExp[] = [
  // "read / review / look at / show me / summarize / open / have a look at THE/THIS/A ... (prehearing statement | document | file | statement | PHS | etc)"
  /\b(read|review|look\s+at|show\s+me|summari[sz]e|summary\s+of|open|have\s+a\s+look\s+at|go\s+through)\s+(the|this|that|these|those|a|an|my)\s+(prehearing\s+statement|pre[-\s]?hearing\s+statement|phs|document|doc|file|statement|pdf|attachment|upload|intake|packet)\b/i,
  // Generic "please read it / it"
  /\b(please\s+)?(read|review|look\s+at|summari[sz]e)\s+(it|through\s+it)\b/i,
  // "what is / what are / how does / how do I (write|draft|file|submit) / tell me about / explain / define / do you know what ..."
  /\bwhat\s+(is|are)\s+(a|an|the)\b/i,
  /\bhow\s+do\s+i\s+(write|draft|prepare|file|submit)\s+(a|an)\b/i,
  /\bhow\s+does\s+\w+\s+work\b/i,
  /\btell\s+me\s+(about|what|how|why)\b/i,
  /\b(can|could)\s+you\s+(explain|tell\s+me)\b/i,
  /\b(define|definition\s+of)\b/i,
  /\bexplain\s+(what|how|the\s+difference)\b/i,
  /\bdo\s+you\s+know\s+what\s+(a|an|the)\b/i,
  /\bi\s+(want|would\s+like)\s+to\s+show\s+you\b/i,
  // Spanish informational
  /\bqu[eé]\s+es\s+(un|una|el|la)\b/i,
  /\bc[oó]mo\s+(se\s+)?(escribe|redacta|prepara)\s+(un|una)\b/i,
  /\bexplicame\b/i,
  /\bdime\s+(qu[eé]|c[oó]mo|por\s+qu[eé])\b/i,
  /\bl[eé]e\s+(el|la|este|esta|mi)\s+(declaraci[oó]n|documento|archivo)\b/i,
];

/**
 * Meta-feedback / tool-commentary patterns. These describe the user TALKING
 * ABOUT the tool (bug reports, improvement requests, complaints, "it
 * created a new doc when I didn't ask") rather than requesting one. A
 * keyword match combined with any of these must NOT fire the drafter.
 */
// Phase 0 hardening: window quantifiers capped at 30 (was 60–80) so meta
// guards stay scoped to a single clause. Negation guard is applied at the
// top-level matchLegalDocTrigger via hasNegationAround.
const LEGAL_DOC_META_FEEDBACK_PATTERNS: RegExp[] = [
  /\b(feedback|bug|issue|problem|error|complaint|improvement|improve)\b[\s\S]{0,30}\b(tool|workflow|generator|creator|agent|prest0n|system|feature|wizard|flow|drafter)\b/i,
  /\b(tool|workflow|generator|creator|agent|prest0n|feature|wizard|flow|drafter)\b[\s\S]{0,30}\b(didn[’']?t\s+work|does(?:\s*n[’']?t|\s+not)\s+work|not\s+working|broken|failed|wrong|buggy|bugged|has\s+a\s+bug|is\s+broken|created\s+a\s+new|made\s+a\s+new|generated\s+a\s+new)\b/i,
  /\b(generator|tool|workflow|drafter|creator)\b[\s\S]{0,30}\b(created|generated|made|produced)\s+(a\s+)?(new\s+)?(document|doc|draft|statement|file)\b/i,
  /\bstuck\s+in\s+a\s+loop\b/i,
  /\binfinite\s+loop\b/i,
  /\bi\s+(was|am)\s+(trying|asking)\s+to\s+\w+[\s\S]{0,30}\b(but|however|instead)\b[\s\S]{0,30}\b(it|you|prest0n|the\s+(tool|workflow|generator))\b/i,
  // Spanish
  /\bretroalimentaci[oó]n\b/i,
  /\bno\s+quer[ií]a\b/i,
  /\bla\s+herramienta\s+(no\s+funcion[oó]|fall[oó]|est[aá]\s+rota)\b/i,
  /\b(la\s+herramienta|el\s+generador|el\s+flujo|prest0n)\s+(cre[oó]|gener[oó]|hizo)\s+(un|una|otro|otra)\s+(documento|doc|borrador)\b/i,
];

function hasReadOrMetaGuard(text: string): boolean {
  for (const rx of LEGAL_DOC_READ_OR_META_PATTERNS) if (rx.test(text)) return true;
  return false;
}

function hasMetaFeedbackAboutTool(text: string): boolean {
  for (const rx of LEGAL_DOC_META_FEEDBACK_PATTERNS) if (rx.test(text)) return true;
  return false;
}

/**
 * Litigation argument / motion-drafting requests are not template-intake
 * requests. They often contain generic words like "legal document" or
 * incidental form names like "I-589" inside phrases such as "motion to
 * pretermit I-589", but the user's intent is to draft/analyze an argument
 * from the attached record — not to open the Legal Document Creator picker.
 */
function hasLitigationArgumentOrMotionIntent(text: string): boolean {
  const lower = (text || '').toLowerCase();

  const litigationIntent =
    /\blegal\s+argu(?:ment|ment)\b/.test(lower) ||
    /\b(argument|brief|memorandum|memo\s+of\s+points|points\s+and\s+authorities)\b[\s\S]{0,30}\b(court|judge|motion|opposition|reply|filing)\b/.test(lower) ||
    /\b(court|judge|motion|opposition|reply|filing)\b[\s\S]{0,30}\b(argument|brief|memorandum|memo\s+of\s+points|points\s+and\s+authorities)\b/.test(lower) ||
    /\bopposition\s+(motion|brief|filing)\b/.test(lower) ||
    /\bmotion\s+to\s+pretermit\b/.test(lower) ||
    /\bpretermit\b/.test(lower) ||
    /\bdeport\s+via\s+aca\b/.test(lower) ||
    /\basylum\s+cooperative\s+agreement\b/.test(lower);

  if (!litigationIntent) return false;

  // Preserve true template requests even if the user also mentions argument
  // quality. These are the document types this workflow is built to handle.
  const explicitTemplateRequest =
    /\bqme\b|advocacy[\s.-]?memo|qme[\s.-]?memo/.test(lower) ||
    /\bwcab\b|rule\s+10563|workers[\s.-]?comp|work(ers)?'?\s+compensation/.test(lower) ||
    /immigration\s+(court\s+)?pre[-\s]?hearing\s+statement|prehearing\s+statement|\bphs\b/.test(lower) ||
    /demand\s+letter|carta\s+de\s+demanda|cease\s+and\s+desist/.test(lower) ||
    /employment\s+agreement|non[-\s]?disclosure|\bnda\b|contractor\s+agreement/.test(lower) ||
    /asylum\s+application|solicitud\s+de\s+asilo/.test(lower);

  return !explicitTemplateRequest;
}

/**
 * Test whether a message affirmatively requests the creation of a legal
 * document. This is a broader sweep than `findAffirmativeLegalDocDraftIntent`
 * because it also accepts simple verb+document-type constructions without
 * any "the following" / "using these" context — the legal-doc trigger path
 * is the last line of defence before we route the user to the drafter.
 */
function hasAffirmativeLegalDocIntent(text: string): boolean {
  if (findAffirmativeLegalDocDraftIntent(text)) return true;

  // Simple "VERB + (a|an|the) + DOC_TYPE" constructions — e.g. "draft a
  // WCAB pre-hearing statement", "make an NDA", "prepare an asylum
  // application", "crear un contrato de trabajo".
  const simpleVerbDoc =
    /\b(create|draft|prepare|generate|write|make|start|begin|put\s+together|build|file)\s+(me\s+)?(a|an|the|another|one|my|our)\s+(new\s+|another\s+)?(wcab\s+)?(legal\s+|immigration\s+)?(pre[-\s]?hearing\s+statement|prehearing\s+statement|phs|statement|filing|pleading|demand\s+letter|legal\s+document|nda|non[-\s]?disclosure|employment\s+agreement|contractor\s+agreement|asylum\s+application|i[-\s]?589|qme\s+(advocacy\s+)?memo|advocacy\s+memo|contrato(\s+de\s+trabajo)?|acuerdo|carta\s+de\s+demanda|declaraci[oó]n\s+previa|solicitud\s+de\s+asilo)\b/i;

  if (simpleVerbDoc.test(text)) return true;

  // "I (want|need|would like) a ..." affirmative
  const iWantDoc =
    /\bi\s+(want|need|would\s+like|require)\s+(to\s+\w+\s+)?(a|an|the|another)\s+(new\s+)?(wcab\s+)?(legal\s+|immigration\s+)?(pre[-\s]?hearing\s+statement|prehearing\s+statement|phs|statement|filing|pleading|demand\s+letter|legal\s+document|nda|non[-\s]?disclosure|employment\s+agreement|contractor\s+agreement|asylum\s+application|i[-\s]?589|qme\s+(advocacy\s+)?memo|advocacy\s+memo)\b/i;

  if (iWantDoc.test(text)) return true;

  // Spanish "necesito / quiero" + document type
  const esNecesito =
    /\b(necesito|quiero|requiero)\s+(un|una|otro|otra|el|la)\s+(nuev[oa]\s+)?(declaraci[oó]n\s+previa|documento\s+legal|phs|carta\s+de\s+demanda|acuerdo|contrato(\s+de\s+trabajo)?|nda|solicitud\s+de\s+asilo|memorando\s+qme)\b/i;
  if (esNecesito.test(text)) return true;

  // ------------------------------------------------------------
  // PR #67 / Miles 2026-05-09 — general-legal-document drafter intent.
  //
  // Bug: "a formal complaint to my neighbor" (and similar noun-phrase
  // requests for non-canonical documents — motions, declarations,
  // notices, letters of demand to a specific recipient, etc.) was not
  // recognized as affirmative draft intent because the existing
  // simpleVerbDoc/iWantDoc lists hard-coded only the 8 canonical doc
  // types. The result was that the trigger gate fell through, the
  // request was routed to a non-drafter tool (research / freeform), and
  // the user got markdown links instead of native PDF/DOCX downloads.
  //
  // Fix:
  //   (a) verb + (a|an|the) + <generic doc noun>  →  affirmative
  //   (b) noun-phrase + recipient/target          →  affirmative
  //   (c) Spanish "necesito una queja a/contra ..." etc.
  // ------------------------------------------------------------
  // Verb list covers English drafting verbs plus common Spanish forms
  // and the most-used conjugations (imperative "redacta", "crea",
  // "prepara", "escribe", "haz"; first-person "redacto"). Bare stems
  // ("redact", "redactar") still match anything starting with those
  // letters via the broader keyword. Keep this loose — false positives
  // here are still gated by the read/meta guards in matchLegalDocTrigger.
  const verbGenericDoc = new RegExp(
    '\\b(create|draft|prepare|generate|write|make|start|begin|put\\s+together|build|file|' +
      'redact|redactar|redacta|redacte|redacten|crear|crea|cree|creen|preparar|prepara|prepare|preparen|escribir|escribe|escriba|escriban|generar|genera|genere|generen|hacer|haz|haga|hagan|presentar|presenta|presente|presenten' +
      ')\\s+(me\\s+)?(a|an|the|another|one|my|our|un|una|unos|unas|otro|otra|el|la|los|las|mi|nuestr[oa])\\s+' +
      '(?:[a-zA-Záéíóúñ-]+\\s+){0,2}' +
      GENERAL_DOC_NOUN_SOURCE +
      '\\b',
    'i'
  );
  if (verbGenericDoc.test(text)) return true;

  // Noun-phrase with explicit recipient/target — strongest signal that
  // the user wants a document drafted, even without a leading verb.
  const nounPhraseWithRecipient = new RegExp(
    GENERAL_DOC_NOUN_PHRASE_WITH_RECIPIENT_SOURCE,
    'i'
  );
  if (nounPhraseWithRecipient.test(text)) return true;

  return false;
}

/**
 * Split a message into clauses for independent affirmative-intent checking.
 *
 * Splits on sentence boundaries and common conjunctions — "then",
 * "and then", "after that", commas, semicolons, periods, "but". This lets
 * "read the prehearing statement then draft one for me" route correctly:
 * the "read the prehearing statement" clause is a read-intent (which on
 * its own would block), and the "draft one for me" clause is affirmative
 * (which wins). A standalone "how do I write a prehearing statement" has
 * only one clause, and that clause is a learn-about phrasing, so the
 * overall message is correctly rejected even though "write" is a draft
 * verb.
 */
function splitIntoClauses(text: string): string[] {
  return text
    .split(/\s*(?:\bthen\b|\band\s+then\b|\bafter\s+(?:that|which)\b|\bbut\b|[;,.]\s+|\.\s*$)\s*/i)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
}

// Phase 0 hardening: anchor regex used by `hasNegationAround` to detect
// in-clause negation phrases ("don't draft a contract", "I'm not asking
// for a legal document") in the user's message. Applied AFTER the
// button-ID short-circuit and meta-feedback guard so direct button
// invocations and meta complaints still behave correctly.
const LEGAL_DOC_NEGATION_ANCHOR =
  /\b(legal\s+doc(?:ument)?|contract|nda|agreement|demand\s+letter|letter|statement|asylum|i[-\s]?589|phs|qme|memo)\b/i;

function matchLegalDocTrigger(text: string): { matches: boolean } {
  // 1. Button-ID payloads always match (direct UI invocations bypass every
  //    natural-language guard).
  if (Object.values(BUTTON_IDS).some((id) => text.includes(id))) {
    return { matches: true };
  }

  if (!text || !text.trim()) return { matches: false };

  // 1b. Phase 0 negation guard. If the user explicitly says they are NOT
  //     asking for a legal document near any anchor keyword, suppress the
  //     match before any keyword check runs.
  if (hasNegationAround(text, LEGAL_DOC_NEGATION_ANCHOR)) return { matches: false };

  // 2. Meta-feedback about the tool must NEVER fire the drafter, no
  //    matter how many trigger keywords appear.
  if (hasMetaFeedbackAboutTool(text)) return { matches: false };

  // 2b. Generic litigation argument / motion drafting is not a supported
  //     template flow. Let the main Prest0n session or research workflow handle
  //     it instead of showing the NDA/employment/contractor template picker.
  if (hasLitigationArgumentOrMotionIntent(text)) return { matches: false };

  // 3. Must have at least one legal-doc trigger keyword somewhere.
  const keywordMatches = LEGAL_DOC_TRIGGER_PATTERN.test(text);
  if (!keywordMatches) return { matches: false };

  // 4. Clause-level disambiguation. A message matches iff at least one of
  //    its clauses contains affirmative draft intent AND that clause is
  //    not itself a read/learn-about phrasing. This correctly handles:
  //      - "please read the prehearing statement" → only clause is
  //        read-intent → no match.
  //      - "how do I write a prehearing statement" → only clause is
  //        learn-about (which covers the "how do I write" verb form) → no
  //        match despite the bare "write" verb.
  //      - "read the prehearing statement then draft one for me" → two
  //        clauses. The second ("draft one for me") is a bare affirmative
  //        verb with a pronoun stand-in; it matches via
  //        `hasBareAffirmativeVerbWithProform` when the overall message
  //        already has a doc-type keyword.
  const clauses = splitIntoClauses(text);
  for (const clause of clauses) {
    if (hasReadOrMetaGuard(clause)) continue;
    if (hasAffirmativeLegalDocIntent(clause)) {
      return { matches: true };
    }
  }

  // 5. Bare affirmative draft verb with a pronoun stand-in ("draft one for
  //    me", "create another", "put one together") in a clause that is NOT
  //    a read/learn-about phrasing, combined with a doc-type keyword
  //    somewhere in the message, also matches. This is the
  //    "read the PHS then draft one for me" pattern.
  const bareAffirmativeVerbWithProform =
    /\b(create|draft|prepare|generate|write|make|start|begin|put\s+together|build|file)\s+(me\s+)?(one|another|it|this|that|another\s+one|one\s+for\s+me|it\s+for\s+me|one\s+up|up\s+one)\b/i;
  for (const clause of clauses) {
    if (hasReadOrMetaGuard(clause)) continue;
    if (bareAffirmativeVerbWithProform.test(clause)) {
      return { matches: true };
    }
  }

  // 6. Fallback: if the whole-message affirmative check passes AND the
  //    whole message does not read as a read/learn-about phrasing, accept.
  //    This catches affirmative constructions that may span clause
  //    boundaries (belt-and-suspenders; avoids regression).
  if (hasAffirmativeLegalDocIntent(text) && !hasReadOrMetaGuard(text)) {
    return { matches: true };
  }

  return { matches: false };
}

// Exported for unit testing and to share with the orchestrator prompt
// reinforcement path if future changes need the same guard list.
export const __legalDocMatchInternals = {
  LEGAL_DOC_TRIGGER_PATTERN,
  LEGAL_DOC_READ_OR_META_PATTERNS,
  LEGAL_DOC_META_FEEDBACK_PATTERNS,
  splitIntoClauses,
  hasReadOrMetaGuard,
  hasMetaFeedbackAboutTool,
  hasLitigationArgumentOrMotionIntent,
  hasAffirmativeLegalDocIntent,
  matchLegalDocTrigger,
};

// ============================================================================
// Template Detection Helpers
// ============================================================================

function detectTemplateIdFromText(text: string): string | null {
  const lower = text.toLowerCase();

  // Check button IDs
  if (lower.includes(BUTTON_IDS.employmentAgreement)) return 'employment-agreement';
  if (lower.includes(BUTTON_IDS.nda)) return 'nda';
  if (lower.includes(BUTTON_IDS.contractorAgreement)) return 'contractor-agreement';
  if (lower.includes(BUTTON_IDS.qmeAdvocacyMemo)) return 'qme-advocacy-memo';
  if (lower.includes(BUTTON_IDS.wcabPreHearingStatement)) return 'wcab-pre-hearing-statement';
  if (lower.includes(BUTTON_IDS.immigrationPreHearingStatement)) return 'immigration-pre-hearing-statement';
  if (lower.includes(BUTTON_IDS.i589Asylum)) return 'i-589-asylum';
  if (lower.includes(BUTTON_IDS.generalLegalDocument)) return 'general-legal-document';

  const hasPreHearingIntent =
    /pre[-\s]?hearing\s+statement/.test(lower) ||
    /prehearing\s+statement/.test(lower) ||
    /\bphs\b/.test(lower) ||
    /declaraci[oó]n\s+previa/.test(lower);

  const hasImmigrationPhsContext =
    /\brespondent\b/.test(lower) ||
    /\bimmigration\s+(court|judge|proceedings?|hearing)\b/.test(lower) ||
    /\bremoval\s+proceedings?\b/.test(lower) ||
    /\bwithholding\s+of\s+removal\b/.test(lower) ||
    /\bcat\b|convention\s+against\s+torture/.test(lower) ||
    /\basylum\b/.test(lower) ||
    /\brespondent\s+seeks\b/.test(lower);

  const hasWcabPhsContext =
    /rule\s+10563/.test(lower) ||
    /wcab.*pre[-\s]?hearing/.test(lower) ||
    /\bworkers[\s.-]?comp/.test(lower) ||
    /\bwork(ers)?'?\s+compensation\b/.test(lower) ||
    /\binjured\s+worker\b/.test(lower) ||
    /\bdate\s+of\s+injury\b/.test(lower) ||
    /\bmpn\b/.test(lower) ||
    /\btemporary\s+disability\b/.test(lower) ||
    /\bemployer\b/.test(lower);

  // Immigration court pre-hearing statements must not be routed to WCAB merely
  // because they contain "pre-hearing statement." This is the I-589 non-trigger
  // regression path: asylum/withholding/removal PHS => immigration PHS, not I-589
  // and not WCAB Rule 10563.
  if (hasPreHearingIntent && hasImmigrationPhsContext && !hasWcabPhsContext) {
    return 'immigration-pre-hearing-statement';
  }

  // WCAB Pre-Hearing Statement / Rule 10563 patterns
  if (hasPreHearingIntent || hasWcabPhsContext) {
    return 'wcab-pre-hearing-statement';
  }

  // I-589 / Asylum application patterns.
  // Keep this narrower than incidental asylum/withholding mentions inside pre-hearing statements.
  if (
    /\bi[-\s]?589\b/.test(lower) ||
    /\bform\s+i[-\s]?589\b/.test(lower) ||
    /\basylum\s+application\b/.test(lower) ||
    /\bprepare\s+(an?\s+)?asylum\s+application\b/.test(lower) ||
    /\bfile\s+(an?\s+)?asylum\s+application\b/.test(lower) ||
    /\bhelp\s+(me\s+)?prepar(e|ing)\s+.*asylum\b/.test(lower) ||
    /solicitud\s+de\s+asilo/.test(lower)
  ) {
    return 'i-589-asylum';
  }

  // QME / Workers' Comp memo patterns.
  // 2026-05-06 guard (Task 3 / FH-CLASSIFIER-005): QME/advocacy-memo keywords
  // leaked from prior history or attachment content MUST NOT override
  // current-message PHS intent. Only allow QME routing when the current
  // text does NOT express pre-hearing statement intent, AND the current
  // message explicitly says QME / med-legal advocacy memo / workers' comp memo.
  if (
    !hasPreHearingIntent &&
    (
      /\bqme\b/.test(lower) ||
      /advocacy[\s.-]?memo/.test(lower) ||
      /qme[\s.-]?memo/.test(lower) ||
      /workers[\s.-]?comp.*memo/.test(lower) ||
      /workers[\s.-]?compensation.*memo/.test(lower) ||
      /memo\s+medico/.test(lower) ||
      /memorando\s+qme/.test(lower)
    )
  ) {
    return 'qme-advocacy-memo';
  }

  // Demand letter patterns must run before generic employment detection.
  // Prompts like "draft a demand letter for an employment matter" should not
  // become employment agreements merely because they contain "employment".
  if (
    /demand\s+letter/.test(lower) ||
    /carta\s+de\s+demanda/.test(lower) ||
    /cease\s+and\s+desist/.test(lower) ||
    /cese\s+y\s+desist/.test(lower)
  ) {
    return 'demand-letter';
  }

  // Employment agreement patterns. Keep narrower than any employment-related
  // legal matter so retaliation/wage demand letters do not route here.
  if (
    /employment\s+agreement/.test(lower) ||
    /employee\s+agreement/.test(lower) ||
    /hire/.test(lower) ||
    /contrato\s+de\s+trabajo/.test(lower)
  ) {
    return 'employment-agreement';
  }

  // NDA patterns
  if (
    /\bnda\b/.test(lower) ||
    /non[-\s]?disclosure/.test(lower) ||
    /acuerdo\s+de\s+no\s+divulgaci/.test(lower) ||
    /confidentiality\s+agreement/.test(lower)
  ) {
    return 'nda';
  }

  // Contractor patterns
  if (
    /\bcontractor\b/.test(lower) ||
    /\bfreelance\b/.test(lower) ||
    /\bindependent\s+contractor\b/.test(lower) ||
    /contratista/.test(lower)
  ) {
    return 'contractor-agreement';
  }

  // ------------------------------------------------------------
  // Layer C fallback (Miles 2026-05-08, widened 2026-05-09):
  //
  // Route to the freeform general-document path when ANY of the
  // following patterns are present in the request text:
  //   1. drafting verb + generic document noun
  //      ("draft a motion to continue trial")
  //   2. noun-phrase + explicit recipient/target slot, no verb required
  //      ("a formal complaint to my neighbor",
  //       "a notice of breach to ABC Corp",
  //       "una queja a mi vecino")
  //
  // Pattern 2 is the 2026-05-09 regression fix: PR #67 shipped the
  // general-document drafter but the detector required a leading draft
  // verb. "a formal complaint to my neighbor" matched neither the 8
  // canonical templates nor pattern 1, so the message fell through to
  // the orchestrator and Miles got markdown links from a research-style
  // reply instead of native PDF/DOCX downloads.
  // ------------------------------------------------------------
  const draftVerbRx =
    /\b(create|draft|prepare|generate|write|make|start|begin|put\s+together|build|file|redact|redactar|crear|preparar|escribir|generar|hacer|presentar)\b/i;
  const docNounRx = new RegExp('\\b' + GENERAL_DOC_NOUN_SOURCE + '\\b', 'i');
  if (draftVerbRx.test(lower) && docNounRx.test(lower)) {
    return 'general-legal-document';
  }

  // Noun-phrase + recipient slot (no verb required).
  const nounPhraseWithRecipientRx = new RegExp(
    GENERAL_DOC_NOUN_PHRASE_WITH_RECIPIENT_SOURCE,
    'i'
  );
  if (nounPhraseWithRecipientRx.test(lower)) {
    return 'general-legal-document';
  }

  return null;
}

function getRequiredFieldsList(template: LegalTemplate, language: 'en' | 'es' = 'en'): string[] {
  return template.fields
    .filter((f) => f.required)
    .map((f) => {
      const fieldLabel = language === 'es' && f.labelEs ? `${f.labelEs} / ${f.label}` : f.label;
      const optionsHint =
        f.options && f.options.length > 0
          ? ` (${language === 'es' ? 'opciones' : 'options'}: ${f.options.map((o) => language === 'es' && o.labelEs ? `${o.labelEs} / ${o.label}` : o.label).join(', ')})`
          : f.placeholder
          ? ` (e.g. ${f.placeholder})`
          : '';
      return `${fieldLabel}${optionsHint}`;
    });
}

// ============================================================================
// Claude Field Parser
// ============================================================================

async function parseFieldsWithClaude(params: {
  template: LegalTemplate;
  userInput: string;
  existingFields: Record<string, string>;
  signal?: AbortSignal;
}): Promise<Record<string, string>> {
  // D14 slice 2 (2026-08-26 ruling follow-through): the browser sends only
  // the route NAME ('legal-doc-creation'); the server stamps the SSOT ladder
  // + effort (anthropicProxy mode:'task'). Slice 7 (2026-09-15) retired the
  // deploy-skew fallback and the legacy model constant with it.

  const fieldDescriptions = params.template.fields
    .map((f) => `- ${f.id}: ${f.label} (${f.type}${f.required ? ', required' : ''})`)
    .join('\n');

  const systemPrompt = `You are a legal document assistant. Extract field values from the user's input.

Template fields:
${fieldDescriptions}

Already collected: ${JSON.stringify(params.existingFields)}

Respond ONLY with a valid JSON object mapping field IDs to values. Only include fields that have clear values in the user's input. Use empty string for unclear fields. Do not include fields already in existingFields unless the user provided updated values.

Example: {"employer_name": "Acme Corp", "start_date": "2026-04-01"}`;

  const proxyUrl = getAnthropicProxyUrl();

  // Safety guard 2026-04-29: for multi-doc packets the extracted text can exceed 200k chars,
  // which can cause Anthropic proxy timeouts even though Sonnet's context handles it. Truncate
  // at 180k chars so we always get a response back; this is still plenty of evidence for the
  // field extractor to surface applicant/employer/DOI/issues.
  const MAX_USER_INPUT_CHARS = 180_000;
  const trimmedUserInput = params.userInput.length > MAX_USER_INPUT_CHARS
    ? params.userInput.slice(0, MAX_USER_INPUT_CHARS) + '\n\n[… input truncated for field-extraction; full text remains in conversation context for downstream drafting.]'
    : params.userInput;

  console.log('[LegalDocCreator] parseFieldsWithClaude input:', params.userInput.length, 'chars', params.userInput.length > MAX_USER_INPUT_CHARS ? '(TRUNCATED to 180k)' : '(full)');

  // max_tokens raised 2026-04-29: templates like WCAB PHS have ~20 fields, and long-text
  // fields (issues_in_dispute, factual_summary) can push the JSON response past 1024 tokens,
  // causing truncation -> JSON.parse failure -> "Extracted 0 fields" fallback. 8192 gives
  // plenty of headroom for any template we currently ship.
  const parseBaseBody = {
    max_tokens: 8192,
    stream: true,
    stream_options: { include_usage: true },
    system: systemPrompt,
    messages: [{ role: 'user', content: trimmedUserInput }],
  };

  let response: Response | null = null;
  try {
    response = await fetch(proxyUrl, {
      method: 'POST',
      // Who is calling — the signed-in user's ID token (security slice 6, ../../proxyAuth).
      headers: { 'Content-Type': 'application/json', 'X-Prest0n-Task': 'legal-doc-creation', ...(await proxyAuthHeaders()) },
      body: JSON.stringify({ mode: 'task', route: 'legal-doc-creation', ...parseBaseBody }),
      signal: params.signal,
    });
  } catch {
    response = null;
  }

  // D14 slice 7 (2026-09-15): the deploy-skew fallback retired — field
  // parsing is best-effort by design, so a route failure degrades to the
  // existing empty-fields path below.
  if (!response) {
    console.warn('[LegalDocCreator] Field parsing transport failed (network error)');
    return {};
  }
  if (!response.ok) {
    console.warn('[LegalDocCreator] Field parsing proxy returned non-OK:', response.status);
    return {};
  }

  let fullText = '';
  try {
    const streamResult = await readStreamedText(response);
    fullText = streamResult.text;
  } catch (streamErr) {
    console.warn('[LegalDocCreator] Field parsing stream error:', streamErr);
    return {};
  }
  console.log('[LegalDocCreator] Field parsing raw response:', fullText.substring(0, 500));

  if (!fullText.trim()) {
    console.warn('[LegalDocCreator] Field parsing returned empty response');
    return {};
  }

  try {
    const jsonMatch = fullText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.warn('[LegalDocCreator] No JSON object found in response:', fullText.substring(0, 200));
      return {};
    }
    const raw = JSON.parse(jsonMatch[0]) as Record<string, unknown>;
    // Coerce all values to strings (Claude may return booleans, numbers, etc.)
    const result: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v != null) result[k] = String(v);
    }
    console.log('[LegalDocCreator] Parsed fields:', JSON.stringify(result));
    return result;
  } catch (parseErr) {
    console.warn('[LegalDocCreator] JSON parse failed:', parseErr, fullText.substring(0, 200));
    return {};
  }
}

// ============================================================================
// Multi-document direct-draft helpers
// ============================================================================

const MULTI_DOC_DIRECT_DRAFT_MIN_FILES = 5;
const SOURCE_DOCUMENT_ANALYSIS_FIELD = '__source_document_analysis';

export const __legalDocPromptInternals = {
  buildDocumentSystemPrompt,
  buildDocumentUserPrompt,
  SOURCE_DOCUMENT_ANALYSIS_FIELD,
};

function shouldDirectDraftFromMultiDocPacket(templateId: string | undefined | null, fileCount: number): boolean {
  if (!templateId || fileCount < MULTI_DOC_DIRECT_DRAFT_MIN_FILES) return false;
  // Direct-draft allowlist (2026-05-06 v2): any template whose fields are routinely
  // buried inside intake packets of 5+ documents should never bounce the user back to
  // manually re-type information that is provable from the uploaded documents.
  // I-589 is included because Fernando's EOIR packets routinely include the I-589 itself
  // (first/middle/last name, DOB, nationality, A-number, narrative) and the wizard was
  // still re-asking for first/last name even after the packet was extracted.
  return (
    templateId === 'immigration-pre-hearing-statement' ||
    templateId === 'wcab-pre-hearing-statement' ||
    templateId === 'demand-letter' ||
    templateId === 'i-589-asylum' ||
    templateId === 'qme-advocacy-memo'
  );
}

function markMissingRequiredFieldsForAttorneyReview(
  template: LegalTemplate,
  fields: Record<string, string>
): Record<string, string> {
  const updated = { ...fields };
  for (const field of template.fields) {
    if (field.required && !updated[field.id]) {
      updated[field.id] = '[ATTORNEY REVIEW REQUIRED — not clearly found in uploaded documents]';
    }
  }
  return updated;
}

// ============================================================================
// Document Generation Prompt Builders
// ============================================================================

function buildDocumentSystemPrompt(
  template: LegalTemplate,
  language: 'en' | 'es'
): string {
  const langNote =
    language === 'es'
      ? 'Generate the entire document in Spanish.'
      : 'Generate the document in English.';

  const templateSpecificNotes: Record<string, string> = {
    'employment-agreement':
      '- Note California at-will employment doctrine (Labor Code section 2922).\n- Note that non-compete clauses are largely unenforceable in California under SB 699 (Business and Professions Code 16600).',
    nda: '- Ensure the confidentiality obligations are clear and enforceable under California law.',
    'contractor-agreement':
      '- Include California AB5 (Assembly Bill 5) independent contractor status provisions.\n- Address the ABC test for independent contractor classification.\n- Note that misclassification of employees as contractors has significant legal consequences.',
    'qme-advocacy-memo':
      '- Reference California Labor Code sections 4060–4062, which govern the QME process for disputed workers\' compensation claims.\n- Affirm the applicant\'s right to a comprehensive, thorough, and impartial medical evaluation.\n- The memo should maintain a professional tone while clearly and forcefully advocating for the injured worker\'s position.\n- Address all disputed issues directly and provide the applicant\'s position on causation, apportionment, and future medical necessity.',
    'wcab-pre-hearing-statement':
      '- Draft a California WCAB Pre-Hearing Statement compliant with Cal. Code Regs., title 8, section 10563.\n- Include all six required sections: (1) identity of all parties, (2) issues in dispute, (3) stipulations, (4) evidence to be presented, (5) witnesses expected to testify, and (6) estimated time for hearing.\n- Use uploaded/intake facts when available and clearly mark unknown or incomplete facts as [ATTORNEY REVIEW REQUIRED].\n- When source documents are provided, include a numbered "## Document Index" near the top with entries in the form "Doc N — <file/source name> — <date if known> — <1-line summary>".\n- When source documents are provided, every factual assertion in the body must end with a parenthetical citation in the form "(Doc N, <file/source name>)"; if multiple sources support the same fact, cite each source in the same parenthetical.\n- Do not invent claim numbers, insurance carriers, medical records, witnesses, stipulations, or hearing estimates that are not provided.',
    'immigration-pre-hearing-statement':
      '- Draft an EOIR Immigration Court Pre-Hearing Statement for removal proceedings.\n- The statement may support asylum, withholding of removal, CAT withholding/protection, humanitarian asylum, and related defenses when those are supported by the uploaded documents.\n- Use the uploaded source-document analysis as the primary record. Extract respondent names, A-numbers, court/order details, relief sought, PSG/nexus theories, facts, exhibits, witness information, and disputed issues from those documents.\n- If a sample pre-hearing statement is included, follow its Word-style structure, caption conventions, ordering, and tone as closely as possible while adapting the facts to the current client(s).\n- Begin with a numbered "## Document Index" near the top with entries in the form "Doc N — <file/source name> — <date if known> — <1-line summary>".\n- Every factual assertion in the body must end with a parenthetical citation in the form "(Doc N, <file/source name>)"; if multiple sources support the same fact, cite each source in the same parenthetical.\n- Never ask the user to re-type facts that appear in the uploaded packet. If a required fact is not clearly supported by the packet, draft with [ATTORNEY REVIEW REQUIRED] instead of blocking.\n- Do not invent A-numbers, hearing dates, judge names, court addresses, exhibit contents, PSGs, citations, witnesses, or procedural facts not supported by the uploaded packet.',
  };

  const specificNote = templateSpecificNotes[template.id] || '';

  return `You are a professional legal document drafter. Generate a complete, professional legal document based on the provided template and field values.

Requirements:
- ${langNote}
- Use proper legal prose appropriate for California jurisdiction
- Include all standard legal provisions for this document type
- Use ## for section headers (numbered 1., 2., 3., etc.)
- Use ### for subsection headers
- Include proper signature blocks at the end (see SIGNATURE BLOCK FORMAT below)
- Add a disclaimer: "This document was generated for informational purposes only. Consult a licensed attorney before use."
- Start with # [Document Title] as the first line
- Keep the language professional but clear
- All fields not provided should be marked as "[TO BE COMPLETED]"

OUTPUT FORMAT — STRICT:
- DO NOT use markdown tables. Never emit pipe characters (\`|\`), table separator rows (e.g. \`|---|---|\`), or empty header rows (\`|||\`) anywhere in the document. The renderer does not support markdown tables and any pipe characters will appear as raw text in the generated PDF.
- For ANY tabular information (signature blocks, party info, etc.), emit plain-text lines with the label, a colon, and the value on the same line, like this:

  Signature:        _________________________
  Printed Name:     _________________________
  Title / Capacity: _________________________
  Date:             _________________________
  Address:          555 Fifth Avenue, Suite 1200
                    New York, NY 10017
  Email:            [TO BE COMPLETED]
  Phone:            [TO BE COMPLETED]

  (Wrap continuation lines — like the second line of Address — by indenting them so they align under the value column.)

SIGNATURE BLOCK FORMAT:
- For each party, first emit a single-line party identifier such as \`**Party 1 — <name>**\` or the party's role + name on its own line.
- Then emit the signature, printed name, title/capacity, date, address, email, and phone as plain-text lines in the "Label: value" format shown above — NOT as a markdown table.
- Use underscores (\`_________________________\`) for blank signature/name/date lines that the party will fill in by hand.
- Separate each party's signature block with a blank line.
${specificNote}`;
}

function buildDocumentUserPrompt(
  template: LegalTemplate,
  fields: Record<string, string>,
  language: 'en' | 'es'
): string {
  const fieldsSummary = template.fields
    .map((f) => `${f.label}: ${fields[f.id] || '[TO BE COMPLETED]'}`)
    .join('\n');

  const sourceDocumentAnalysis = fields[SOURCE_DOCUMENT_ANALYSIS_FIELD]
    ? `\n\nUploaded Source Document Analysis / Extracted Packet Context:\n${fields[SOURCE_DOCUMENT_ANALYSIS_FIELD]}`
    : '';

  const multiDocSynthesisRules = fields[SOURCE_DOCUMENT_ANALYSIS_FIELD]
    ? `\n\nMULTI-DOCUMENT SYNTHESIS CONTRACT (HARD RULE):\n- Include a numbered \"## Document Index\" near the top of the document (after the title and any attorney-review banner).\n- The index must be numbered Doc 1..Doc N and each line must include: file/source name, date if available, and a one-line summary.\n- Every factual assertion in the body must include a parenthetical source citation using this exact format: \"(Doc N, <file/source name>)\".\n- If a fact is supported by multiple documents, cite all supporting docs in the same parenthetical.\n- If a required fact is not clearly supported by the source packet, use [ATTORNEY REVIEW REQUIRED] and list it in Missing Facts. Do not fabricate citations.`
    : '';

  const langInstruction =
    language === 'es'
      ? 'Generate the complete document in Spanish.'
      : 'Generate the complete document in English.';

  return `Please generate a complete ${template.name} with the following information:

${fieldsSummary}${sourceDocumentAnalysis}${multiDocSynthesisRules}

${langInstruction}

Generate all sections from the template structure:
${template.sections.map((s) => `- ${s.title}`).join('\n')}

Include a full signature block at the end with signature lines for all parties. Render signature blocks as plain-text "Label: value" lines (NOT as markdown tables — do not use pipe characters).`;
}

// ============================================================================
// Freeform Legal Drafter (Layer B — Miles 2026-05-08)
//
// Used when the selected template is 'general-legal-document' OR a dynamic
// (user/tenant/global) saved template with isFreeform=true. The drafter
// infers document structure from the stated purpose, infers a conventional
// filename, and mirrors the user's active language at generation time.
// ============================================================================

/**
 * Default freeform-drafter system prompt. Saved reusable templates may
 * supply their own EN/ES prompts; when absent, this default is used.
 */
function defaultFreeformDrafterSystemPrompt(language: 'en' | 'es', requiresAttorneyReview: boolean): string {
  const langClause =
    language === 'es'
      ? 'Generate the entire document in natural, professional Spanish (legal register appropriate to California / U.S. practice). Every heading, section title, and signature block label must be in Spanish.'
      : 'Generate the document in natural, professional English (legal register appropriate to California / U.S. practice).';

  const banner = requiresAttorneyReview
    ? `\n- Begin the document with a one-line attorney-review banner: "[ATTORNEY REVIEW REQUIRED — draft only]".`
    : `\n- DO NOT include an "[ATTORNEY REVIEW REQUIRED]" banner. The attorney reviewing this draft already knows their own workflow; an extra banner adds noise.`;

  return `You are a seasoned, acclaimed California attorney drafting a legal document.

A senior associate will review this draft before it is filed or sent, so produce a full, well-organized draft rather than a skeleton. Keep the tone plain-spoken and precise; use legal terminology where it earns its keep.

Task:
- Read the user's stated purpose and any attached/extracted source material carefully.
- Infer the correct document type from the stated purpose (e.g. motion, memorandum, declaration, notice, agreement, subpoena, brief, letter, demand letter, stipulation, discovery response, petition, etc.).
- Infer a conventional, professional document name (e.g. "Motion to Continue Trial", "Declaration of Jane Doe in Support of U-Visa Petition", "Demand for Unpaid Overtime Wages", "Memorandum of Points and Authorities in Opposition to Motion for Summary Judgment"). Emit it as a first-line title using a single '#' markdown heading. This inferred name will also be used as the generated filename.
- Infer the correct section structure for that document type (caption/parties/introduction/argument/conclusion/signature block, etc.) and number sections with ##, with ### for subsections.
- Use proper legal prose. Cite statutes and cases where they would normally appear; use clearly-marked placeholders like [CITATION NEEDED — e.g. Cal. Code Civ. Proc. § ___] when the user's input does not supply a citation.
- When attached source documents were extracted, weave their facts into the draft and cite back to document names or exhibit labels where helpful. Never invent facts not supported by the input.
- Where a required fact is missing, insert an inline placeholder like [TO BE COMPLETED — <short description of what is missing>] and continue drafting.
- ${langClause}${banner}

Output format (STRICT):
- Start with a single '#' level-1 heading that is the conventional document name you inferred. This title IS the filename.
- Use ## for major sections, ### for subsections.
- Do NOT use markdown tables. Never emit pipe characters (\`|\`). Render signature blocks and party-info grids as plain-text "Label: value" lines with underscores for signature lines.
- At the very end of the document, emit two clearly-labeled short sections (in the user's language):
    - ## Assumptions Made  — bulleted list of any legal, factual, or procedural assumptions you made while drafting.
    - ## Missing Facts  — bulleted list of facts an attorney needs to supply before this document can be filed/sent.`;
}

/**
 * Resolve the freeform-drafter system prompt for a given template at the
 * user's active generation-time language. Saved templates carry a bilingual
 * pair (freeformDrafterPromptEn / freeformDrafterPromptEs). When a specific
 * language is missing, fall back to the other language, then to the default.
 */
function resolveFreeformSystemPrompt(template: LegalTemplate, language: 'en' | 'es'): string {
  const requiresAttorneyReview = template.requiresAttorneyReview === true;
  if (language === 'es') {
    if (template.freeformDrafterPromptEs) return template.freeformDrafterPromptEs;
    if (template.freeformDrafterPromptEn) return template.freeformDrafterPromptEn;
  } else {
    if (template.freeformDrafterPromptEn) return template.freeformDrafterPromptEn;
    if (template.freeformDrafterPromptEs) return template.freeformDrafterPromptEs;
  }
  return defaultFreeformDrafterSystemPrompt(language, requiresAttorneyReview);
}

function buildFreeformUserPrompt(
  template: LegalTemplate,
  fields: Record<string, string>,
  language: 'en' | 'es'
): string {
  // The general-legal-document schema has these field ids; for user-saved
  // templates, we fall back to joining all populated fields as 'key: value'.
  const purposeValue = fields['documentPurpose'] || '';
  const partiesValue = fields['parties'] || '';
  const jurisdictionValue = fields['jurisdiction'] || '';
  const keyTermsValue = fields['keyTerms'] || '';
  const deadlineValue = fields['deadline'] || '';

  // For user-saved templates whose schema differs, surface every populated
  // field verbatim so the drafter can pick them up.
  const otherFields = Object.entries(fields)
    .filter(
      ([k, v]) =>
        !!v &&
        k !== SOURCE_DOCUMENT_ANALYSIS_FIELD &&
        ![
          'documentPurpose',
          'parties',
          'jurisdiction',
          'keyTerms',
          'deadline',
          'attachedDocuments',
        ].includes(k)
    )
    .map(([k, v]) => `- ${k}: ${v}`)
    .join('\n');

  const sourceDocumentAnalysis = fields[SOURCE_DOCUMENT_ANALYSIS_FIELD]
    ? `\n\nAttached / extracted source material (from the multi-doc packet):\n${fields[SOURCE_DOCUMENT_ANALYSIS_FIELD]}`
    : '';

  const langInstruction =
    language === 'es'
      ? 'Escribe el documento completo en español.'
      : 'Write the complete document in English.';

  const parts: string[] = [];
  parts.push(`User's stated purpose:\n${purposeValue || '(not provided — infer from context below)'}`);
  if (partiesValue) parts.push(`Parties / participants:\n${partiesValue}`);
  if (jurisdictionValue) parts.push(`Jurisdiction / forum:\n${jurisdictionValue}`);
  if (keyTermsValue) parts.push(`Key terms / facts to include:\n${keyTermsValue}`);
  if (deadlineValue) parts.push(`Deadline / filing date:\n${deadlineValue}`);
  if (otherFields) parts.push(`Additional fields supplied by the user:\n${otherFields}`);

  return `${parts.join('\n\n')}${sourceDocumentAnalysis}\n\n${langInstruction}\n\nReminder: begin with a single '#' level-1 heading that names the document conventionally. That title will be used as the generated filename. End with "## Assumptions Made" and "## Missing Facts" sections as instructed.`;
}

/**
 * Extract a conventional filename from the generated document's first
 * '#'-level heading. Falls back to "Legal Document" if none is found.
 * Strips markdown / trailing punctuation, limits length, and keeps Unicode
 * word characters so Spanish headings ("Moción para Continuar el Juicio")
 * round-trip cleanly.
 */
function inferFilenameFromDocument(content: string, fallback: string): string {
  if (!content) return fallback;
  const firstHeading = content.match(/^#\s+(.+?)\s*$/m);
  if (firstHeading && firstHeading[1]) {
    const raw = firstHeading[1]
      .replace(/^\*+|\*+$/g, '')
      .replace(/[\r\n]+/g, ' ')
      .replace(/[\\/:"*?<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (raw.length >= 3) return raw.slice(0, 120);
  }
  return fallback;
}

// ============================================================================
// Markdown Table Normalization (MT-QA-BUG-007 safety net)
// ============================================================================
//
// The LLM occasionally emits pipe-delimited markdown tables for signature
// blocks and party-detail grids, even though the prompt forbids it. The
// downstream PDF/DOCX renderers (src/utils/legalDocPdf.ts and
// src/utils/legalDocDocx.ts) have no `table` block kind — so markdown table
// rows fall through to the generic `paragraph` kind and render as literal
// `| col | col |` text with visible pipes.
//
// This normalizer detects contiguous pipe-table blocks (including the
// degenerate `|||` empty-header form and standalone `|---|---|` separator
// rows) and rewrites each row into a plain-text "Label:  value" line.
// Continuation rows whose first cell is empty are indented so the value
// column aligns under the previous row's value column.
//
// This runs AS A SAFETY NET: the primary fix is the system-prompt update
// above instructing the LLM to emit plain-text signature blocks. If the LLM
// drifts, this normalizer catches whatever slipped through so the PDF never
// ships with raw pipes.
function isMarkdownTableLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  // Degenerate empty-row form (LLM emits these as table separators / header placeholders)
  if (/^\|+\s*\|*\s*$/.test(trimmed)) return true;
  // Normal table row must start with `|` and contain at least one more `|`
  if (!trimmed.startsWith('|')) return false;
  return (trimmed.match(/\|/g) || []).length >= 2;
}

// Matches a separator row like `|---|---|`, `| --- | :---: |`, `|-|-|`
function isMarkdownTableSeparator(cells: string[]): boolean {
  if (cells.length === 0) return false;
  return cells.every((c) => /^:?-+:?$/.test(c.trim()) || c.trim() === '');
}

function parseTableRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  if (!t) return [];
  return t.split('|').map((c) => c.trim());
}

function renderTableRowsAsPlainText(rows: string[][]): string[] {
  if (rows.length === 0) return [];
  const ncols = Math.max(...rows.map((r) => r.length));
  const labelWidth = Math.max(...rows.map((r) => (r[0] || '').length), 0);
  const LABEL_PAD = Math.min(Math.max(labelWidth, 0), 20);
  const out: string[] = [];
  for (const row of rows) {
    if (ncols === 2) {
      const key = (row[0] || '').trim();
      const val = (row[1] || '').trim();
      if (key && val) {
        out.push(`${key.padEnd(LABEL_PAD, ' ')}  ${val}`);
      } else if (!key && val) {
        out.push(`${' '.repeat(LABEL_PAD)}  ${val}`);
      } else if (key && !val) {
        out.push(key);
      }
    } else {
      const joined = row
        .map((c) => (c || '').trim())
        .filter(Boolean)
        .join('  ');
      if (joined) out.push(joined);
    }
  }
  return out;
}

/**
 * Scan a generated legal document and rewrite any pipe-delimited markdown
 * tables into plain-text "Label:  value" lines. Leaves non-table content
 * untouched. Safe to call on the same content multiple times (idempotent
 * after the first pass — subsequent calls find no table rows to rewrite).
 */
export function normalizeLegalDocMarkdownTables(content: string): string {
  if (!content) return content;
  if (!content.includes('|')) return content;

  const lines = content.split('\n');
  const out: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (!isMarkdownTableLine(lines[i])) {
      out.push(lines[i]);
      i++;
      continue;
    }

    // Collect a contiguous table block. Allow single blank lines BETWEEN
    // table rows (LLMs often pad rows with blanks) but stop at a blank
    // followed by a non-table line.
    const tableLines: string[] = [];
    while (i < lines.length) {
      if (isMarkdownTableLine(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      } else if (
        lines[i].trim() === '' &&
        i + 1 < lines.length &&
        isMarkdownTableLine(lines[i + 1])
      ) {
        i++;
      } else {
        break;
      }
    }

    const rows: string[][] = [];
    for (const tl of tableLines) {
      const cells = parseTableRow(tl);
      if (cells.length === 0) continue;
      if (isMarkdownTableSeparator(cells)) continue;
      if (cells.every((c) => c === '')) continue;
      rows.push(cells);
    }

    if (rows.length === 0) {
      out.push('');
      continue;
    }

    const rendered = renderTableRowsAsPlainText(rows);
    for (const r of rendered) out.push(r);
    out.push('');
  }

  return out.join('\n');
}

// ============================================================================
// Document Generation (Streaming + Continuation + Retry)
// ============================================================================

interface StreamDocumentParams {
  systemPrompt: string;
  userPrompt: string;
  signal?: AbortSignal;
  onChunk: (chunk: string, accumulated: string) => void;
  onComplete: (fullText: string, usage: TokenUsageMetadata | undefined) => void;
  /** Diagnostic context logged at request start/finish. */
  diagnostics?: {
    templateId?: string | null;
    fileCount?: number;
    cacheHit?: boolean;
    promptChars?: number;
  };
}

/**
 * Per-call output ceiling for the document completion. Raised from the
 * long-standing 8192 cap on 2026-05-06 after Fernando's immigration +
 * WCAB Pre-Hearing Statements were truncating mid-sentence. The proxy
 * (`functions/src/index.ts` anthropicProxy) forwards `max_tokens`
 * untouched, and OpenRouter honors it for Sonnet/Opus variants.
 *
 * With continuation (below), this is the *per-pass* ceiling; total
 * output can grow to roughly DOC_MAX_TOKENS_PER_PASS * (MAX_CONTINUATIONS + 1).
 */
const DOC_MAX_TOKENS_PER_PASS = 16384;
const MAX_CONTINUATIONS = 3;
const STREAM_RETRY_DELAY_MS = 1000;

const CONTINUATION_NUDGE =
  'Continue the document exactly where you left off. Do not repeat any ' +
  'content you have already produced. Emit the remaining sections, including ' +
  'the closing attorney-review banner / signature block / Missing Facts list. ' +
  'Preserve the same formatting, headers, and voice.';

type OpenRouterChatMessage = {
  role: 'system' | 'user' | 'assistant';
  content: string;
};

/**
 * Issue one streamed document-generation pass to the Anthropic proxy. Returns
 * the raw stream result including `stopReason` / `finishReason` so the caller
 * can decide whether to run a continuation pass.
 */
async function runDocStreamPass(opts: {
  model: string;
  proxyUrl: string;
  systemPrompt: string;
  messages: OpenRouterChatMessage[];
  signal?: AbortSignal;
  onChunk: (chunk: string, accumulated: string) => void;
}): Promise<StreamReadResult> {
  // The generation pass is the billed call of this tool (the field-parsing
  // and prompt-translation helpers never were): the proxy's server meter
  // debits it (slice 2, 2026-09-15 — see ../../proxyMeter).
  const accountHint = knownAccountHint();
  const response = await fetch(opts.proxyUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...SERVER_METER_HEADERS,
      ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
      // Who is calling — the signed-in user's ID token (security slice 6, ../../proxyAuth).
      ...(await proxyAuthHeaders()),
    },
    body: JSON.stringify({
      model: opts.model,
      max_tokens: DOC_MAX_TOKENS_PER_PASS,
      stream: true,
      stream_options: { include_usage: true },
      system: opts.systemPrompt,
      messages: opts.messages,
      meterSummary: 'legal document generation',
    }),
    signal: opts.signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => 'Unknown error');
    const transient = isTransientHttpStatus(response.status);
    throw new StreamGenerationError({
      message: `Document generation request failed (${response.status}): ${errText.slice(0, 400)}`,
      transient,
      status: response.status,
      requestId: response.headers.get('x-request-id') || undefined,
      openrouterId: response.headers.get('x-openrouter-id') || undefined,
      model: opts.model,
      alreadyStreamedChars: 0,
    });
  }

  return await readStreamedText(response, { onChunk: opts.onChunk });
}

async function streamDocumentGeneration(params: StreamDocumentParams): Promise<void> {
  const model = CLAUDE_FORMATTER_MODEL;
  const proxyUrl = getAnthropicProxyUrl();
  const startTime = Date.now();
  const promptChars = params.userPrompt.length;

  console.log('[LegalDocCreator] streamDocumentGeneration start', {
    templateId: params.diagnostics?.templateId ?? null,
    fileCount: params.diagnostics?.fileCount ?? null,
    cacheHit: params.diagnostics?.cacheHit ?? null,
    promptChars,
    model,
    maxTokensPerPass: DOC_MAX_TOKENS_PER_PASS,
  });

  let accumulatedAll = '';
  const onChunkWithAccum = (chunk: string, accumulatedPass: string) => {
    // `accumulatedPass` is the pass-local running total. Surface the
    // whole-document accumulator to the caller so UI stream.update sees the
    // full text across continuation boundaries.
    const combined = accumulatedAll + accumulatedPass;
    params.onChunk(chunk, combined);
  };

  const messages: OpenRouterChatMessage[] = [
    { role: 'user', content: params.userPrompt },
  ];

  let lastUsage: TokenUsageMetadata | undefined;
  let continuationCount = 0;
  const maxAttempts = MAX_CONTINUATIONS + 1;

  for (let pass = 0; pass < maxAttempts; pass++) {
    let result: StreamReadResult;
    let retried = false;
    // One transient retry per pass
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        result = await runDocStreamPass({
          model,
          proxyUrl,
          systemPrompt: params.systemPrompt,
          messages,
          signal: params.signal,
          onChunk: onChunkWithAccum,
        });
        break;
      } catch (err) {
        const sge = err instanceof StreamGenerationError ? err : null;
        if (sge && sge.transient && !retried) {
          retried = true;
          console.warn('[LegalDocCreator] Transient stream error; retrying once', {
            pass,
            status: sge.status,
            message: sge.message,
            requestId: sge.requestId,
            alreadyStreamedChars: sge.alreadyStreamedChars,
            durationMs: Date.now() - startTime,
          });

          // If the earlier attempt already streamed content to the UI, treat it
          // as the assistant turn and ask for a continuation rather than
          // restarting. Otherwise, simply re-issue the same pass.
          if (sge.alreadyStreamedChars > 0) {
            // accumulatedAll tracked full text; we haven't advanced past this
            // pass, so we can't reconstruct its partial text here. Fall
            // through to a continuation-style recovery: append whatever we
            // already have to the conversation.
            if (accumulatedAll.length > 0) {
              messages.push({ role: 'assistant', content: accumulatedAll });
              messages.push({ role: 'user', content: CONTINUATION_NUDGE });
            }
          }

          await new Promise((r) => setTimeout(r, STREAM_RETRY_DELAY_MS));
          continue;
        }
        throw err;
      }
    }

    const passText = result!.text;
    accumulatedAll += passText;
    if (result!.usage) lastUsage = result!.usage;

    const wasTruncated =
      result!.stopReason === 'max_tokens' || result!.finishReason === 'length';

    console.log('[LegalDocCreator] streamDocumentGeneration pass done', {
      pass,
      passChars: passText.length,
      totalChars: accumulatedAll.length,
      stopReason: result!.stopReason,
      finishReason: result!.finishReason,
      wasTruncated,
      continuationCount,
      durationMs: Date.now() - startTime,
    });

    if (!wasTruncated) break;

    if (continuationCount + 1 > MAX_CONTINUATIONS) {
      console.warn('[LegalDocCreator] Max continuations reached; returning truncated output', {
        continuationCount: MAX_CONTINUATIONS,
        totalChars: accumulatedAll.length,
      });
      break;
    }

    continuationCount += 1;
    if (continuationCount > 1) {
      console.warn('[LegalDocCreator] streamDocumentGeneration continuation count > 1', {
        continuationCount,
        totalChars: accumulatedAll.length,
      });
    }

    // Append the just-streamed text as the assistant turn and prompt for
    // continuation.
    messages.push({ role: 'assistant', content: passText });
    messages.push({ role: 'user', content: CONTINUATION_NUDGE });
  }

  const responseTimeMs = Date.now() - startTime;
  const finalUsage: TokenUsageMetadata | undefined = lastUsage
    ? { ...lastUsage, model, responseTimeMs }
    : { model, responseTimeMs } as TokenUsageMetadata;

  console.log('[LegalDocCreator] streamDocumentGeneration complete', {
    templateId: params.diagnostics?.templateId ?? null,
    totalChars: accumulatedAll.length,
    continuationCount,
    durationMs: responseTimeMs,
  });

  params.onComplete(accumulatedAll, finalUsage);
}

// ============================================================================
// I-589 Summary Text Builder
// ============================================================================

function buildI589SummaryText(fields: Record<string, string>): string {
  const name = [fields['first_name'], fields['middle_name'], fields['last_name']].filter(Boolean).join(' ');
  const lines: string[] = [
    '# I-589 Asylum Application — Data Summary',
    '',
    '## Applicant',
    `- Name: ${name || '[not provided]'}`,
    `- Date of Birth: ${fields['dob'] || '[not provided]'}`,
    `- Nationality: ${fields['nationality'] || '[not provided]'}`,
    `- A-Number: ${fields['a_number'] || 'None'}`,
    '',
    '## Immigration Status',
    `- Last Entry Date: ${fields['last_entry_date'] || '[not provided]'}`,
    `- Last Entry Place: ${fields['last_entry_place'] || '[not provided]'}`,
    `- Status at Entry: ${fields['immigration_status'] || '[not provided]'}`,
    '',
    '## Persecution Claim',
    `- Grounds: ${fields['persecution_grounds'] || '[not provided]'}`,
    `- Past Persecution: ${fields['persecution_past'] === 'true' ? 'Yes' : fields['persecution_past'] === 'false' ? 'No' : '[not provided]'}`,
    `- Fears Return: ${fields['persecution_fear_return'] === 'true' ? 'Yes' : fields['persecution_fear_return'] === 'false' ? 'No' : '[not provided]'}`,
    '',
    '## Narrative',
    fields['persecution_narrative'] || '[not provided]',
    '',
    '---',
    '*I-589 USCIS PDF form and DOCX attorney worksheet attached above.*',
    '*This document was generated for informational purposes only. Consult a licensed immigration attorney before filing.*',
  ];
  return lines.join('\n');
}

// ============================================================================
// Steps
// ============================================================================

// Step 1: Select document type
const selectTypeStep = {
  id: 'select-type',
  name: 'Select Document Type',
  activeDisplayName: 'selecting document type',

  execute: async (
    ctx: StepContext<LegalDocCreatorState>,
    _rawInput: unknown
  ): Promise<StepResult> => {
    const triggerText = ctx.triggerMessage.text;
    const triggerAttachments = Array.isArray(ctx.triggerMessage.attachments)
      ? ctx.triggerMessage.attachments
      : [];

    // Save-as-Reusable-Template button click (Layer D, Miles 2026-05-08):
    // When the trigger text contains the saveAsTemplate BUTTON_ID, skip the
    // picker entirely and jump to the save-as-template step. Requires a
    // prior generatedContent in state — without that there is nothing to
    // save.
    if (triggerText && triggerText.includes(BUTTON_IDS.saveAsTemplate)) {
      if (!ctx.state.generatedContent) {
        const lang = ctx.state.documentLanguage || 'en';
        ctx.messaging.showWarning(
          lang === 'es' ? 'Nada que guardar' : 'Nothing to save',
          lang === 'es'
            ? 'No hay documento generado reciente para guardar como plantilla. Genera un documento primero.'
            : 'No recent generated document to save as a template. Generate a document first.'
        );
        return { status: 'completed', nextStep: null };
      }
      return {
        status: 'completed',
        nextStep: 'save-as-template',
        carryForward: {
          templateId: ctx.state.selectedTemplate,
          language: ctx.state.documentLanguage || 'en',
        },
      };
    }

    // 2026-05-06 (Task 3 / FH-CLASSIFIER-005): When fresh attachments arrive,
    // reset any stale legal-doc workflow state from a prior case/packet so the
    // new request starts clean — no leaked selectedTemplate, collectedFields,
    // originalPacketFileCount, or generatedContent.
    if (triggerAttachments.length > 0) {
      ctx.setState({
        selectedTemplate: null,
        collectedFields: {},
        originalPacketFileCount: undefined,
        generatedContent: undefined,
        isGenerating: false,
        savePending: undefined,
        generatedTitle: undefined,
      });
    }

    // Check if the trigger text already implies a specific template
    let templateId = detectTemplateIdFromText(triggerText);

    // Log template detection for debugging same-thread multi-case regressions
    console.log('[LegalDocCreator] selectTypeStep template detection', {
      detectedTemplate: templateId ?? null,
      triggerText: triggerText?.slice(0, 200) ?? '(empty)',
      attachmentNames: triggerAttachments.map((f: File) => f.name),
      attachmentCount: triggerAttachments.length,
    });

    // Detect language from the trigger text
    const lowerTriggerText = (triggerText || '').toLowerCase();
    const detectedLanguage: 'en' | 'es' =
      /\b(spanish|espa[nñ]ol|en\s+espa[nñ]ol|crear|solicitud|asilo)\b/.test(lowerTriggerText)
        ? 'es'
        : 'en';

    // If template found from trigger, skip to gather-details. Resolve
    // against static first, then dynamic (user/tenant/global) scopes so
    // buttons for saved templates also route directly here.
    if (!templateId && triggerText) {
      // Direct match on a template id anywhere in the trigger text (a click
      // on a dynamic template button sends the id as trigger text).
      try {
        const dyn = await getTemplateByIdAnyScope(triggerText.trim());
        if (dyn) {
          templateId = dyn.id;
        }
      } catch {
        /* non-fatal */
      }
    }
    if (templateId) {
      const template = getTemplateById(templateId) || (await getTemplateByIdAnyScope(templateId).catch(() => undefined));
      if (template) {
        ctx.setState({
          selectedTemplate: templateId,
          collectedFields: {},
          documentLanguage: detectedLanguage,
          isGenerating: false,
        });
        const successTitle = detectedLanguage === 'es'
          ? `${template.name} seleccionado`
          : `${template.name} selected`;
        const successMsg = detectedLanguage === 'es'
          ? 'Le guiaré a través de la información requerida.'
          : 'I will guide you through the required information.';
        ctx.messaging.showSuccess(successTitle, successMsg);
        return {
          status: 'completed',
          carryForward: { templateId },
        };
      }
    }

    // If user already responded (awaiting-input resume)
    if (ctx.userInput !== undefined) {
      if (isCancelCommand(ctx.userInput)) {
        ctx.messaging.showSpecialMessage({
          header: { title: 'cancelled', icon: '✕' },
          sections: [{ type: 'text', content: 'Legal document creation cancelled.' }],
          theme: 'custom',
          customColors: LEGAL_DOC_THEME,
        });
        return { status: 'completed', nextStep: null };
      }

      templateId = detectTemplateIdFromText(ctx.userInput);
      if (templateId) {
        const template = getTemplateById(templateId);
        if (template) {
          const lowerUserInput = ctx.userInput.toLowerCase();
          const userLang: 'en' | 'es' =
            /\b(spanish|espa[nñ]ol|en\s+espa[nñ]ol|crear|solicitud|asilo)\b/.test(lowerUserInput)
              ? 'es'
              : 'en';
          ctx.setState({
            selectedTemplate: templateId,
            collectedFields: {},
            documentLanguage: userLang,
            isGenerating: false,
          });
          return {
            status: 'completed',
            carryForward: { templateId },
          };
        }
      }

      // Dynamic templates: try to resolve a user-saved / firm-saved /
      // global template by id OR by name match before falling through to
      // the "not recognized" warning.
      try {
        const dynMatch = await getTemplateByIdAnyScope((ctx.userInput || '').trim());
        if (dynMatch) {
          ctx.setState({
            selectedTemplate: dynMatch.id,
            collectedFields: {},
            documentLanguage: ctx.state.documentLanguage || 'en',
            isGenerating: false,
          });
          return {
            status: 'completed',
            carryForward: { templateId: dynMatch.id },
          };
        }
      } catch {
        /* non-fatal — fall through to warning */
      }

      // Still unclear — show warning and re-prompt
      ctx.messaging.showWarning(
        'Document type not recognized',
        "I didn't recognize that document type. Please choose from the options below."
      );
    }

    // Dynamic saved-template buttons (Layer D Miles 2026-05-08). Loaded
    // best-effort; empty array on any Firestore error.
    let dynamicTemplateButtons: { label: string; id: string }[] = [];
    try {
      const all = await loadAllAvailableTemplates();
      // Filter out the static ones we already render explicitly below so we
      // don't double-list them.
      const staticIds = new Set([
        'employment-agreement',
        'nda',
        'contractor-agreement',
        'qme-advocacy-memo',
        'wcab-pre-hearing-statement',
        'immigration-pre-hearing-statement',
        'i-589-asylum',
        'demand-letter',
        'general-legal-document',
      ]);
      dynamicTemplateButtons = all
        .filter((t) => !staticIds.has(t.id))
        .map((t) => ({
          label: detectedLanguage === 'es' && t.nameEs ? t.nameEs : t.name,
          // Using the raw template id as the button id works because
          // detectTemplateIdFromText is case-insensitive on static ids and
          // the user-input branch above will route to getTemplateByIdAnyScope.
          id: t.id,
        }));
    } catch {
      /* non-fatal — render picker with static buttons only */
    }

    // Show document type selection UI
    ctx.messaging.showSpecialMessage({
      header: { title: 'legal document creator', icon: '\u2696' },
      sections: [
        { type: 'text', content: 'Select the type of legal document to create:' },
        ...(SHOW_LEGAL_DOC_ESCAPE_HATCH
          ? [
              {
                type: 'text' as const,
                content:
                  "If that's not what you had in mind, just say *cancel* and we can go a different direction.",
              },
            ]
          : []),
      ],
      buttons: [
        { label: 'Employment Agreement', id: BUTTON_IDS.employmentAgreement },
        { label: 'Non-Disclosure Agreement (NDA)', id: BUTTON_IDS.nda },
        { label: 'Independent Contractor Agreement', id: BUTTON_IDS.contractorAgreement },
        { label: 'QME Advocacy Memo (Workers\' Comp)', id: BUTTON_IDS.qmeAdvocacyMemo },
        { label: 'WCAB Pre-Hearing Statement (Rule 10563)', id: BUTTON_IDS.wcabPreHearingStatement },
        { label: 'Immigration Court Pre-Hearing Statement', id: BUTTON_IDS.immigrationPreHearingStatement },
        { label: 'I-589 Asylum Application (USCIS PDF + Worksheet)', id: BUTTON_IDS.i589Asylum },
        {
          label: detectedLanguage === 'es'
            ? 'Documento general (describe lo que necesitas)'
            : 'General document (describe what you need)',
          id: BUTTON_IDS.generalLegalDocument,
        },
        ...dynamicTemplateButtons,
      ],
      theme: 'custom',
      customColors: LEGAL_DOC_THEME,
      helperText: 'Or type the document type below',
    });

    return { status: 'awaiting-input' };
  },
};

// Step 2: Gather field details
const gatherDetailsStep = {
  id: 'gather-details',
  name: 'Gather Document Details',
  activeDisplayName: 'collecting information',

  execute: async (
    ctx: StepContext<LegalDocCreatorState>,
    rawInput: unknown
  ): Promise<StepResult> => {
    const input = rawInput as { templateId?: string } | undefined;

    // Get templateId from carryForward or state
    const templateId = input?.templateId || ctx.state.selectedTemplate;
    if (!templateId) {
      ctx.messaging.showError(
        'No template selected',
        'Please start over and select a document type.'
      );
      return { status: 'completed', nextStep: null };
    }

    // Resolve against static first, then dynamic (user/tenant/global) so
    // saved reusable templates work in gather-details the same way as
    // built-in templates.
    let template = getTemplateById(templateId);
    if (!template) {
      try {
        template = await getTemplateByIdAnyScope(templateId);
      } catch {
        /* fall through — will error below if still undefined */
      }
    }
    if (!template) {
      ctx.messaging.showError(
        'Template not found',
        `Could not find template: ${templateId}. Please try again.`
      );
      return { status: 'completed', nextStep: null };
    }

    // Handle user input (resume from awaiting-input)
    if (ctx.userInput !== undefined) {
      if (isCancelCommand(ctx.userInput)) {
        ctx.messaging.showSpecialMessage({
          header: { title: 'cancelled', icon: '✕' },
          sections: [{ type: 'text', content: 'Legal document creation cancelled.' }],
          theme: 'custom',
          customColors: LEGAL_DOC_THEME,
        });
        return { status: 'completed', nextStep: null };
      }

      // Check for uploaded documents — extract raw text directly via mammoth/text extraction
      // then feed into field parser for mapping
      let combinedInput = ctx.userInput;
      const uploadedFiles = ctx.userAttachments;
      if (uploadedFiles && uploadedFiles.length > 0 && hasAnalyzableFiles(uploadedFiles)) {
        try {
          const docLang = ctx.state.documentLanguage || 'en';

          ctx.messaging.showSuccess(
            docLang === 'es' ? 'Leyendo documento...' : 'Reading document...',
            docLang === 'es'
              ? `Analizando ${uploadedFiles.length} archivo(s) para extraer datos.`
              : `Analyzing ${uploadedFiles.length} file(s) to extract data.`
          );

          // Extract raw text from files directly (bypass Claude vision for text-extractable formats)
          const rawTexts: string[] = [];
          for (const file of uploadedFiles) {
            try {
              const lowerName = file.name.toLowerCase();
              if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                  lowerName.endsWith('.docx')) {
                // DOCX: use mammoth via dynamic import
                const mammoth = await import('mammoth');
                const arrayBuffer = await file.arrayBuffer();
                const result = await mammoth.extractRawText({ arrayBuffer });
                if (result.value) {
                  rawTexts.push(result.value);
                  console.log('[LegalDocCreator] DOCX raw text extracted, length:', result.value.length);
                }
              } else if (
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.type === 'application/vnd.oasis.opendocument.spreadsheet' ||
                lowerName.endsWith('.xlsx') ||
                lowerName.endsWith('.xls') ||
                lowerName.endsWith('.xlsm') ||
                lowerName.endsWith('.ods')
              ) {
                // XLSX / XLS / ODS: use SheetJS via dynamic import
                const XLSX = await import('xlsx');
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
                const sections: string[] = [];
                for (const sheetName of workbook.SheetNames) {
                  const sheet = workbook.Sheets[sheetName];
                  if (!sheet) continue;
                  const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
                  if (csv && csv.trim()) sections.push(`[Sheet: ${sheetName}]\n${csv}`);
                }
                const combined = sections.join('\n\n');
                if (combined.trim()) {
                  rawTexts.push(combined);
                  console.log('[LegalDocCreator] XLSX extracted, length:', combined.length);
                }
              } else if (
                file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                file.type === 'application/vnd.ms-powerpoint' ||
                file.type === 'application/vnd.oasis.opendocument.presentation' ||
                lowerName.endsWith('.pptx') ||
                lowerName.endsWith('.ppt') ||
                lowerName.endsWith('.odp')
              ) {
                // PPTX / PPT / ODP: use JSZip to walk slide XMLs
                const JSZip = (await import('jszip')).default;
                const zip = await JSZip.loadAsync(await file.arrayBuffer());
                const slideEntries = Object.keys(zip.files)
                  .filter((n: string) => /^ppt\/slides\/slide\d+\.xml$/.test(n))
                  .sort((a: string, b: string) => {
                    const ai = parseInt(a.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
                    const bi = parseInt(b.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
                    return ai - bi;
                  });
                const slideTexts: string[] = [];
                for (let i = 0; i < slideEntries.length; i++) {
                  const entry = zip.files[slideEntries[i]];
                  if (!entry) continue;
                  const xml = await entry.async('string');
                  const matches = Array.from(xml.matchAll(/<a:t[^>]*>([\s\S]*?)<\/a:t>/g));
                  const texts = matches
                    .map((m: RegExpMatchArray) => m[1]
                      .replace(/<[^>]+>/g, ' ')
                      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
                      .replace(/\s+/g, ' ').trim())
                    .filter((t: string) => t.length > 0);
                  if (texts.length) slideTexts.push(`[Slide ${i + 1}]\n${texts.join('\n')}`);
                }
                const combined = slideTexts.join('\n\n');
                if (combined.trim()) {
                  rawTexts.push(combined);
                  console.log('[LegalDocCreator] PPTX extracted, length:', combined.length);
                }
              } else if (file.type.startsWith('text/') || lowerName.endsWith('.txt')) {
                const text = await file.text();
                if (text) {
                  rawTexts.push(text);
                  console.log('[LegalDocCreator] Text file extracted, length:', text.length);
                }
              } else {
                // For PDFs and images, fall back to readDocuments (uses Claude vision)
                const extractPrompt = docLang === 'es'
                  ? 'Extrae toda la información personal, migratoria y de asilo de este documento. Incluye: nombres, fechas, direcciones, números A, estados migratorios, narrativas de persecución.'
                  : 'Extract all personal, immigration, and asylum-related information from this document. Include: names, dates, addresses, A-numbers, immigration statuses, persecution narratives.';

                const docResult = await readDocuments({
                  files: [file],
                  query: extractPrompt,
                  signal: ctx.abortSignal,
                  sharedContext: ctx.sharedContext,
                  recordTransaction: ctx.recordTransaction,
                  source: 'workflow',
                });
                if (docResult.analysis && docResult.filesAnalyzed > 0) {
                  rawTexts.push(docResult.analysis);
                  console.log('[LegalDocCreator] File analyzed via readDocuments, analysis length:', docResult.analysis.length);
                }
              }
            } catch (fileErr) {
              console.warn('[LegalDocCreator] Failed to extract from file:', file.name, fileErr);
            }
          }

          if (rawTexts.length > 0) {
            const extractedText = rawTexts.join('\n\n---\n\n');
            combinedInput = `[EXTRACTED FROM UPLOADED DOCUMENT]\n${extractedText}\n\n[USER INPUT]\n${ctx.userInput}`;
            console.log('[LegalDocCreator] Combined input with extracted text, total length:', combinedInput.length);
          }
        } catch (docErr) {
          console.warn('[LegalDocCreator] Document extraction failed:', docErr);
        }
      }

      // Use Claude to parse fields from user input (+ extracted document text if available)
      const existingFields = { ...ctx.state.collectedFields };

      let parsedFields: Record<string, string> = {};
      try {
        parsedFields = await parseFieldsWithClaude({
          template,
          userInput: combinedInput,
          existingFields,
          signal: ctx.abortSignal,
        });
      } catch (err) {
        console.warn('[LegalDocCreator] Field parsing failed:', err);
      }

      // Detect language preference
      let language = ctx.state.documentLanguage || 'en';
      const lowerInput = ctx.userInput.toLowerCase();
      if (/\b(spanish|espa[nñ]ol|en\s+espa[nñ]ol)\b/.test(lowerInput)) language = 'es';
      else if (/\b(english|ingl[eé]s|in\s+english)\b/.test(lowerInput)) language = 'en';

      // Merge new fields into collected fields (only non-empty values)
      // Claude may return booleans/numbers instead of strings — coerce everything to string
      const updatedFields: Record<string, string> = { ...existingFields };
      for (const [key, rawValue] of Object.entries(parsedFields)) {
        if (rawValue == null) continue;
        const value = String(rawValue).trim();
        if (value) {
          updatedFields[key] = value;
        }
      }

      ctx.setState({
        selectedTemplate: templateId,
        collectedFields: updatedFields,
        documentLanguage: language,
        isGenerating: false,
      });

      // Check if all required fields are collected.
      // 2026-05-06 v2: also honor a prior-turn packet size. When a user resumes the workflow
      // after the initial extraction (e.g. by typing a follow-up without re-uploading the
      // same 9 PDFs), the direct-draft guard should still fire so we do not fall back to
      // the form wizard asking for fields buried in the previously extracted packet.
      const uploadedFileCount = uploadedFiles?.length || 0;
      const effectivePacketFileCount = Math.max(uploadedFileCount, ctx.state.originalPacketFileCount || 0);
      const resumedWithPriorExtraction =
        (ctx.state.collectedFields && ctx.state.collectedFields[SOURCE_DOCUMENT_ANALYSIS_FIELD]) ||
        combinedInput.includes('[EXTRACTED FROM UPLOADED DOCUMENT]');
      if (
        shouldDirectDraftFromMultiDocPacket(templateId, effectivePacketFileCount) ||
        (resumedWithPriorExtraction && shouldDirectDraftFromMultiDocPacket(templateId, MULTI_DOC_DIRECT_DRAFT_MIN_FILES))
      ) {
        const directDraftFields = markMissingRequiredFieldsForAttorneyReview(template, updatedFields);
        if (combinedInput.includes('[EXTRACTED FROM UPLOADED DOCUMENT]')) {
          directDraftFields[SOURCE_DOCUMENT_ANALYSIS_FIELD] = combinedInput;
        } else if (
          ctx.state.collectedFields &&
          ctx.state.collectedFields[SOURCE_DOCUMENT_ANALYSIS_FIELD] &&
          !directDraftFields[SOURCE_DOCUMENT_ANALYSIS_FIELD]
        ) {
          directDraftFields[SOURCE_DOCUMENT_ANALYSIS_FIELD] = ctx.state.collectedFields[SOURCE_DOCUMENT_ANALYSIS_FIELD];
        }
        ctx.setState({
          selectedTemplate: templateId,
          collectedFields: directDraftFields,
          documentLanguage: language,
          isGenerating: false,
          originalPacketFileCount: effectivePacketFileCount,
        });
        return {
          status: 'completed',
          carryForward: { templateId, fields: directDraftFields, language },
        };
      }

      const missingFields = template.fields.filter(
        (f) => f.required && !updatedFields[f.id]
      );

      if (missingFields.length === 0) {
        // All required fields collected — proceed to generate
        return {
          status: 'completed',
          carryForward: { templateId, fields: updatedFields, language },
        };
      }

      // Still have missing fields — show what's still needed
      const missingList = missingFields.map((f) => {
        const fieldLabel = language === 'es' && f.labelEs ? `${f.labelEs} / ${f.label}` : f.label;
        const optionsHint =
          f.options && f.options.length > 0
            ? ` (choose: ${f.options.map((o) => language === 'es' && o.labelEs ? `${o.labelEs} / ${o.label}` : o.label).join(', ')})`
            : f.placeholder
            ? ` (e.g. ${f.placeholder})`
            : '';
        return `${fieldLabel}${optionsHint}`;
      });

      // Show collected so far + what's still needed
      const collectedCount = Object.keys(updatedFields).length;
      const collectedNote =
        collectedCount > 0
          ? language === 'es'
            ? `He recopilado ${collectedCount} campo${collectedCount !== 1 ? 's' : ''} hasta ahora. `
            : `I have collected ${collectedCount} field${collectedCount !== 1 ? 's' : ''} so far. `
          : '';

      const moreInfoText = language === 'es'
        ? 'Por favor proporcione la siguiente información requerida:'
        : 'Please provide the following remaining required information:';
      const stepByStepText = language === 'es'
        ? 'Puede proporcionar toda la información a la vez o continuar respondiendo paso a paso.'
        : 'You can provide all information at once or continue answering piece by piece.';
      // FH-ESCAPE-006: always-visible escape hatch. Users can leave the
      // workflow at any time without hunting for a stop button.
      const escapeHatchText = SHOW_LEGAL_DOC_ESCAPE_HATCH
        ? language === 'es'
          ? 'Si no es lo que tenía en mente, solo escriba *cancelar* y podemos ir en otra dirección.'
          : "If that's not what you had in mind, just say *cancel* and we can go a different direction."
        : '';
      const helperText = language === 'es' ? 'Escriba sus respuestas abajo' : 'Type your answers below';

      ctx.messaging.showSpecialMessage({
        header: {
          title: `${template.name.toLowerCase()} — ${language === 'es' ? 'se necesita más información' : 'more information needed'}`,
          icon: '\uD83D\uDCCB',
        },
        sections: [
          {
            type: 'text',
            content: `${collectedNote}${moreInfoText}`,
          },
          { type: 'list', content: missingList },
          {
            type: 'text',
            content: stepByStepText,
          },
          ...(escapeHatchText
            ? [
                {
                  type: 'text' as const,
                  content: escapeHatchText,
                },
              ]
            : []),
        ],
        theme: 'custom',
        customColors: LEGAL_DOC_THEME,
        helperText,
      });

      return {
        status: 'awaiting-input',
        carryForward: { templateId },
      };
    }

    // Use language already detected by select-type step, or detect from trigger
    let initialLanguage: 'en' | 'es' = ctx.state.documentLanguage || 'en';
    if (initialLanguage === 'en') {
      const lowerTrigger = (ctx.userInput ?? '').toLowerCase();
      if (/\b(spanish|espa[nñ]ol|en\s+espa[nñ]ol|crear|solicitud|asilo)\b/.test(lowerTrigger)) {
        initialLanguage = 'es';
      }
    }
    ctx.setState({ documentLanguage: initialLanguage });

    // Check if the trigger came with attachments — if so, process them immediately
    const triggerFiles = ctx.triggerMessage?.attachments;
    if (triggerFiles && triggerFiles.length > 0 && hasAnalyzableFiles(triggerFiles)) {
      try {
        ctx.messaging.showSuccess(
          initialLanguage === 'es' ? 'Leyendo documento...' : 'Reading document...',
          initialLanguage === 'es'
            ? `Analizando ${triggerFiles.length} archivo(s) para extraer datos automáticamente.`
            : `Analyzing ${triggerFiles.length} file(s) to extract data automatically.`
        );

        // Extract raw text directly for text-extractable formats.
        // Fix (2026-04-29): previously looped per-file through readDocuments() which caused
        // silent per-file failures on large packets (5+ PDFs) and produced "Extracted 0 fields."
        // Now we split into two passes: (1) local-extractable files (docx/txt) read in parallel;
        // (2) all remaining binary files (PDFs/images) handed to readDocuments() in ONE batch call.
        // The extraction prompt is also template-aware so WC/Employment/NDA packets extract the
        // right fields instead of the immigration-oriented default.
        const rawTexts: string[] = [];
        const binaryFiles: File[] = [];

        await Promise.all(
          triggerFiles.map(async (file) => {
            try {
              const lowerName = file.name.toLowerCase();
              if (
                file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                lowerName.endsWith('.docx')
              ) {
                const mammoth = await import('mammoth');
                const arrayBuffer = await file.arrayBuffer();
                const result = await mammoth.extractRawText({ arrayBuffer });
                if (result.value) rawTexts.push(`=== ${file.name} ===\n${result.value}`);
              } else if (
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                file.type === 'application/vnd.ms-excel' ||
                file.type === 'application/vnd.oasis.opendocument.spreadsheet' ||
                lowerName.endsWith('.xlsx') ||
                lowerName.endsWith('.xls') ||
                lowerName.endsWith('.xlsm') ||
                lowerName.endsWith('.ods')
              ) {
                // XLSX / XLS / ODS: SheetJS via dynamic import
                const XLSX = await import('xlsx');
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(new Uint8Array(arrayBuffer), { type: 'array' });
                const sections: string[] = [];
                for (const sheetName of workbook.SheetNames) {
                  const sheet = workbook.Sheets[sheetName];
                  if (!sheet) continue;
                  const csv = XLSX.utils.sheet_to_csv(sheet, { blankrows: false });
                  if (csv && csv.trim()) sections.push(`[Sheet: ${sheetName}]\n${csv}`);
                }
                const combined = sections.join('\n\n');
                if (combined.trim()) rawTexts.push(`=== ${file.name} ===\n${combined}`);
              } else if (
                file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                file.type === 'application/vnd.ms-powerpoint' ||
                file.type === 'application/vnd.oasis.opendocument.presentation' ||
                lowerName.endsWith('.pptx') ||
                lowerName.endsWith('.ppt') ||
                lowerName.endsWith('.odp')
              ) {
                // PPTX / PPT / ODP: JSZip slide-XML walk
                const JSZip = (await import('jszip')).default;
                const zip = await JSZip.loadAsync(await file.arrayBuffer());
                const slideEntries = Object.keys(zip.files)
                  .filter((n: string) => /^ppt\/slides\/slide\d+\.xml$/.test(n))
                  .sort((a: string, b: string) => {
                    const ai = parseInt(a.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
                    const bi = parseInt(b.match(/slide(\d+)\.xml/)?.[1] ?? '0', 10);
                    return ai - bi;
                  });
                const slideTexts: string[] = [];
                for (let i = 0; i < slideEntries.length; i++) {
                  const entry = zip.files[slideEntries[i]];
                  if (!entry) continue;
                  const xml = await entry.async('string');
                  const matches = Array.from(xml.matchAll(/<a:t[^>]*>([\s\S]*?)<\/a:t>/g));
                  const texts = matches
                    .map((m: RegExpMatchArray) => m[1]
                      .replace(/<[^>]+>/g, ' ')
                      .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
                      .replace(/&quot;/g, '"').replace(/&apos;/g, "'")
                      .replace(/\s+/g, ' ').trim())
                    .filter((t: string) => t.length > 0);
                  if (texts.length) slideTexts.push(`[Slide ${i + 1}]\n${texts.join('\n')}`);
                }
                const combined = slideTexts.join('\n\n');
                if (combined.trim()) rawTexts.push(`=== ${file.name} ===\n${combined}`);
              } else if (file.type.startsWith('text/') || lowerName.endsWith('.txt')) {
                const text = await file.text();
                if (text) rawTexts.push(`=== ${file.name} ===\n${text}`);
              } else {
                binaryFiles.push(file);
              }
            } catch (fileErr) {
              console.warn('[LegalDocCreator] Local text extraction failed for', file.name, fileErr);
            }
          })
        );

        if (binaryFiles.length > 0) {
          // Build a template-aware extraction prompt so WCAB PHS, Demand Letters, NDAs, etc. each
          // extract fields relevant to their template rather than the legacy immigration-only prompt.
          const fieldHints = template.fields
            .slice(0, 20)
            .map((f) => `- ${f.label}${f.labelEs ? ` (${f.labelEs})` : ''}`)
            .join('\n');
          const extractPrompt = initialLanguage === 'es'
            ? `Estás preparando un borrador de "${template.name}". Extrae toda la información relevante de los archivos adjuntos, con énfasis en los siguientes campos:\n${fieldHints}\n\nTambién incluye cualquier hecho importante, fecha, parte, lesión, citación legal u otra evidencia que un abogado necesitaría para completar este documento.`
            : `You are preparing a draft of a "${template.name}". Extract all relevant information from the attached files, with emphasis on the following fields:\n${fieldHints}\n\nAlso include any key facts, dates, parties, injuries, legal citations, or other evidence an attorney would need to complete this document. If you cannot read a particular file, list its name explicitly so the user knows which file failed.`;

          try {
            const docResult = await readDocuments({
              files: binaryFiles,
              query: extractPrompt,
              signal: ctx.abortSignal,
              sharedContext: ctx.sharedContext,
              recordTransaction: ctx.recordTransaction,
              source: 'workflow',
            });
            if (docResult.analysis && docResult.filesAnalyzed > 0) {
              rawTexts.push(`=== BATCH-READ (${docResult.filesAnalyzed}/${binaryFiles.length} files) ===\n${docResult.analysis}`);
            } else {
              console.warn('[LegalDocCreator] Batch readDocuments returned no analysis for', binaryFiles.length, 'files');
            }
          } catch (batchErr) {
            console.warn('[LegalDocCreator] Batch document read failed:', batchErr);
          }
        }

        const extractedText = rawTexts.join('\n\n');
        if (extractedText.trim()) {
          const parsedFromDoc = await parseFieldsWithClaude({
            template,
            userInput: `[EXTRACTED FROM UPLOADED DOCUMENT]\n${extractedText}`,
            existingFields: {},
            signal: ctx.abortSignal,
          });

          let updatedFields: Record<string, string> = {};
          for (const [key, rawValue] of Object.entries(parsedFromDoc)) {
            if (rawValue == null) continue;
            const value = String(rawValue).trim();
            if (value) updatedFields[key] = value;
          }

          // Fernando demo bugfix 2026-05-06:
          // For large source packets (5+ files) and complex legal documents, do not bounce
          // the user back into the form wizard for fields like respondent name/relief sought.
          // The uploaded packet is the source of truth; generate the draft with attorney-review
          // placeholders for anything not clearly extracted. This prevents the "vicious loop"
          // where users repeatedly say "extract it from the documents" while the wizard keeps
          // asking for fields that may be buried in the packet.
          if (shouldDirectDraftFromMultiDocPacket(templateId, triggerFiles.length)) {
            updatedFields = markMissingRequiredFieldsForAttorneyReview(template, updatedFields);
            updatedFields[SOURCE_DOCUMENT_ANALYSIS_FIELD] = extractedText;
            ctx.setState({ collectedFields: updatedFields, originalPacketFileCount: triggerFiles.length });
            return {
              status: 'completed',
              carryForward: { templateId, fields: updatedFields, language: initialLanguage },
            };
          }

          ctx.setState({ collectedFields: updatedFields });

          const missingFields = template.fields.filter(
            (f) => f.required && !updatedFields[f.id]
          );

          if (missingFields.length === 0) {
            return {
              status: 'completed',
              carryForward: { templateId, fields: updatedFields, language: initialLanguage },
            };
          }

          // Show what was extracted + what's still missing
          const extractedCount = Object.keys(updatedFields).length;
          const missingList = missingFields.map((f) => {
            const fieldLabel = initialLanguage === 'es' && f.labelEs ? `${f.labelEs} / ${f.label}` : f.label;
            return fieldLabel;
          });

          const extractedMsg = initialLanguage === 'es'
            ? `Extraje ${extractedCount} campo(s) del documento. Por favor complete la información faltante:`
            : `Extracted ${extractedCount} field(s) from the document. Please provide the remaining information:`;

          ctx.messaging.showSpecialMessage({
            header: {
              title: initialLanguage === 'es' ? 'datos extraídos del documento' : 'data extracted from document',
              icon: '📄',
            },
            sections: [
              { type: 'text', content: extractedMsg },
              { type: 'list', content: missingList },
            ],
            theme: 'custom',
            customColors: LEGAL_DOC_THEME,
            helperText: initialLanguage === 'es' ? 'Complete los campos faltantes' : 'Fill in the remaining fields',
          });

          return {
            status: 'awaiting-input',
            carryForward: { templateId },
          };
        }
      } catch (docErr) {
        console.warn('[LegalDocCreator] Initial document extraction failed:', docErr);
        // Fall through to normal field prompt
      }
    }

    // First entry — show required fields list
    const requiredFields = getRequiredFieldsList(template, initialLanguage);

    const infoNeededTitle = initialLanguage === 'es'
      ? `${template.name.toLowerCase()} — información necesaria`
      : `${template.name.toLowerCase()} — information needed`;
    const provideInfoText = initialLanguage === 'es'
      ? 'Por favor proporcione la siguiente información para su documento:'
      : 'Please provide the following information for your document:';
    const stepByStepInitial = initialLanguage === 'es'
      ? 'Puede proporcionar toda la información a la vez, responder paso a paso, o subir un documento (formulario de admisión, declaración, solicitud anterior) y extraeré los datos automáticamente.'
      : 'You can provide all information at once, answer step by step, or upload a document (intake form, declaration, prior filing) and I\'ll extract the data automatically.';
    const helperInitial = initialLanguage === 'es' ? 'Escriba sus respuestas o suba un documento' : 'Type your answers or upload a document';

    ctx.messaging.showSpecialMessage({
      header: {
        title: infoNeededTitle,
        icon: '\uD83D\uDCCB',
      },
      sections: [
        {
          type: 'text',
          content: provideInfoText,
        },
        { type: 'list', content: requiredFields },
        {
          type: 'text',
          content: stepByStepInitial,
        },
      ],
      theme: 'custom',
      customColors: LEGAL_DOC_THEME,
      helperText: helperInitial,
    });

    return {
      status: 'awaiting-input',
      carryForward: { templateId },
    };
  },
};

// Step 3: Generate the document
const generateStep = {
  id: 'generate',
  name: 'Generate Document',
  activeDisplayName: 'generating legal document',

  execute: async (
    ctx: StepContext<LegalDocCreatorState>,
    rawInput: unknown
  ): Promise<StepResult> => {
    const input = rawInput as
      | { templateId?: string; fields?: Record<string, string>; language?: 'en' | 'es' }
      | undefined;

    // Resolve parameters from carryForward or state
    const templateId = input?.templateId || ctx.state.selectedTemplate;
    const fields = input?.fields || ctx.state.collectedFields;
    const language = input?.language || ctx.state.documentLanguage || 'en';

    if (!templateId) {
      ctx.messaging.showError(
        'No template selected',
        'Please start over and select a document type.'
      );
      return { status: 'completed', nextStep: null };
    }

    // Resolve against static templates first; fall back to dynamic
    // (user/tenant/global) templates so saved reusable templates can be
    // generated the same way as the built-in ones.
    let template = getTemplateById(templateId);
    if (!template) {
      try {
        template = await getTemplateByIdAnyScope(templateId);
      } catch (resolveErr) {
        console.warn('[LegalDocCreator] Dynamic template resolution failed:', resolveErr);
      }
    }
    if (!template) {
      ctx.messaging.showError(
        'Template not found',
        `Could not find template: ${templateId}. Please try again.`
      );
      return { status: 'completed', nextStep: null };
    }

    ctx.setState({ isGenerating: true });

    const stream = ctx.messaging.showPending(
      'generating legal document',
      'legal_doc_generating'
    );
    let streamFinalized = false;

    try {
      const createdAt = new Date();

      // ---- I-589 special path: fill USCIS PDF + DOCX worksheet ----
      if (templateId === 'i-589-asylum') {
        const i589Data = fieldsToI589Data(fields);
        const applicantName = [fields['first_name'], fields['middle_name'], fields['last_name']]
          .filter(Boolean)
          .join(' ') || 'Applicant';
        // Title format uses "Generated MM/D/YYYY" so the date is unambiguously the
      // document generation date, not a case date (DOI, hearing date, filing date).
      // QA 2026-05-08: Malaika flagged that a bare date next to a party name could
      // be confused with the Date of Injury on the subtitle of a WCAB PHS.
      const docTitle = `I-589 Asylum Application — ${applicantName} (Generated ${createdAt.toLocaleDateString('en-US')})`;

        stream.update({ html: '<p>Filling USCIS I-589 PDF form and generating worksheet...</p>' });

        let pdfFile: File | undefined;
        let docxFile: File | undefined;
        let pdfSerialized: SerializedAttachment | undefined;
        let docxSerialized: SerializedAttachment | undefined;

        try {
          const [pdfBytes, docxFileResult] = await Promise.all([
            fillI589Form(i589Data),
            buildI589DocxWorksheet({ data: i589Data, createdAt, applicantFullName: applicantName, language }),
          ]);

          const dateStr = createdAt.toISOString().slice(0, 10);
          const namePart = applicantName.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
          pdfFile = new File([pdfBytes], `i-589-${namePart}-${dateStr}.pdf`, { type: 'application/pdf' });
          docxFile = docxFileResult;
        } catch (formErr) {
          console.error('[LegalDocCreator] I-589 form fill failed:', formErr);
          throw new Error(`I-589 form fill failed: ${(formErr as Error).message}`);
        }

        // Serialize attachments
        try {
          const auth = getAuth();
          const uid = auth.currentUser?.uid;
          if (uid && pdfFile && docxFile) {
            try {
              const { serializeAttachmentsWithStorage } = await import('../../../utils/attachmentStorage');
              const [ps, ds] = await serializeAttachmentsWithStorage(
                [pdfFile, docxFile],
                uid,
                `i589-${Date.now()}`
              );
              pdfSerialized = ps;
              docxSerialized = ds;
            } catch {
              const [ps, ds] = await Promise.all([serializeFile(pdfFile), serializeFile(docxFile)]);
              pdfSerialized = ps;
              docxSerialized = ds;
            }
          } else if (pdfFile && docxFile) {
            const [ps, ds] = await Promise.all([serializeFile(pdfFile), serializeFile(docxFile)]);
            pdfSerialized = ps;
            docxSerialized = ds;
          }
        } catch (serErr) {
          console.error('[LegalDocCreator] I-589 serialization failed:', serErr);
        }

        const summaryText = buildI589SummaryText(fields);
        ctx.setState({ generatedContent: summaryText });

        storeWorkflowOutput({ content: summaryText, title: docTitle, timestamp: createdAt, source: 'formatted' });

        const attachments: File[] = [];
        const attachmentsSerialized: SerializedAttachment[] = [];
        if (pdfFile) attachments.push(pdfFile);
        if (docxFile) attachments.push(docxFile);
        if (pdfSerialized) attachmentsSerialized.push(pdfSerialized);
        if (docxSerialized) attachmentsSerialized.push(docxSerialized);

        stream.finalize({
          text: summaryText,
          html: formatResearchToHtml(summaryText),
          sourceTag: 'legal_doc_result',
          attachments: attachments.length > 0 ? attachments : undefined,
          attachmentsSerialized: attachmentsSerialized.length > 0 ? attachmentsSerialized : undefined,
          workflowOutput: {
            type: 'formatted_content',
            content: summaryText,
            title: docTitle,
            timestamp: createdAt,
            sourceWorkflow: 'legal-doc-creator',
          },
        });
        streamFinalized = true;
        ctx.setState({ isGenerating: false });
        return { status: 'completed' };
      }

      // ---- Freeform / general-document path (Layer B — Miles 2026-05-08) ----
      // When the resolved template is the 9th static 'general-legal-document'
      // OR a dynamically-loaded user/tenant/global template flagged isFreeform,
      // call the freeform-legal-drafter prompts instead of the standard
      // template-structured prompt builder. The drafter infers the document
      // type, conventional filename, and section order from the user's stated
      // purpose and (when present) attached source material.
      const useFreeformDrafter = template.isFreeform === true;

      const systemPrompt = useFreeformDrafter
        ? resolveFreeformSystemPrompt(template, language)
        : buildDocumentSystemPrompt(template, language);
      const userPrompt = useFreeformDrafter
        ? buildFreeformUserPrompt(template, fields, language)
        : buildDocumentUserPrompt(template, fields, language);

      let generatedText = '';
      let tokenUsage: TokenUsageMetadata | undefined;
      let lastUpdateTime = 0;
      const THROTTLE_MS = 500;

      const diagFileCount =
        ctx.state.originalPacketFileCount ??
        (Array.isArray(ctx.triggerMessage.attachments) ? ctx.triggerMessage.attachments.length : 0);
      const sourceAnalysis = fields[SOURCE_DOCUMENT_ANALYSIS_FIELD] || '';

      await streamDocumentGeneration({
        systemPrompt,
        userPrompt,
        signal: ctx.abortSignal,
        diagnostics: {
          templateId,
          fileCount: diagFileCount,
          cacheHit: undefined,
          promptChars: userPrompt.length + sourceAnalysis.length,
        },
        onChunk: (_chunk, accumulated) => {
          generatedText = accumulated;
          const now = Date.now();
          if (now - lastUpdateTime > THROTTLE_MS) {
            stream.update({ html: formatResearchToHtml(accumulated) });
            lastUpdateTime = now;
          }
        },
        onComplete: (fullText, usage) => {
          generatedText = fullText;
          tokenUsage = usage;
        },
      });

      if (!generatedText.trim()) {
        throw new Error('Document generation produced no output.');
      }

      // MT-QA-BUG-007 safety net: strip any pipe-delimited markdown tables the
      // LLM slipped into the output (signature blocks in particular) and rewrite
      // them as plain-text "Label: value" lines. The PDF/DOCX renderers have no
      // table support; without this, raw `| col | col |` rows ship in the PDF.
      // Idempotent — noop if the prompt worked and no tables are present.
      const renderedText = normalizeLegalDocMarkdownTables(generatedText);
      if (renderedText !== generatedText) {
        console.log(
          '[LegalDocCreator] Normalized markdown tables in generated document (safety net).',
          `before=${generatedText.length} after=${renderedText.length}`
        );
      }

      // Persist generated content + inferred title so the Save-as-Template
      // flow (Layer D) can resume in a subsequent turn without losing the
      // drafter-inferred conventional name.
      ctx.setState({ generatedContent: renderedText });

      // Build document title.
      //
      // Freeform path (Miles 2026-05-08 decision): the drafter emits a
      // conventional filename as its first '#'-level heading (e.g. "Motion to
      // Continue Trial"). We use that inferred name as the document title so
      // the downloaded PDF/DOCX is named "Motion to Continue Trial.pdf" — not
      // "general-document.pdf".
      //
      // Standard path: keep the existing "<Template Name> — <Party> (Generated
      // MM/D/YYYY)" format.
      const partyName =
        fields['employer_name'] ||
        fields['company_name'] ||
        fields['disclosing_party_name'] ||
        fields['applicant_name'] ||
        fields['respondent_name'] ||
        'Generated';
      // Title format uses "Generated MM/D/YYYY" so the date is unambiguously the
      // document generation date, not a case date (DOI, hearing date, filing date).
      // QA 2026-05-08: Malaika flagged on the §10563 WCAB PHS that a bare date next
      // to the party name could be misread as the Date of Injury.
      const inferredName = useFreeformDrafter
        ? inferFilenameFromDocument(renderedText, template.name)
        : '';
      const docTitle = useFreeformDrafter
        ? inferredName || `${template.name} (Generated ${createdAt.toLocaleDateString('en-US')})`
        : `${template.name} — ${partyName} (Generated ${createdAt.toLocaleDateString('en-US')})`;

      if (useFreeformDrafter && inferredName) {
        ctx.setState({ generatedTitle: inferredName });
      }

      // Generate PDF and DOCX in parallel
      let pdfFile: File | undefined;
      let docxFile: File | undefined;
      let pdfSerialized: SerializedAttachment | undefined;
      let docxSerialized: SerializedAttachment | undefined;

      try {
        [pdfFile, docxFile] = await Promise.all([
          buildLegalDocPdf({ content: renderedText, title: docTitle, createdAt }),
          buildLegalDocDocx({ content: renderedText, title: docTitle, createdAt }),
        ]);

        // Try Firebase Storage upload for persistent links
        const auth = getAuth();
        const uid = auth.currentUser?.uid;

        if (uid && pdfFile && docxFile) {
          try {
            const { serializeAttachmentsWithStorage } = await import(
              '../../../utils/attachmentStorage'
            );
            const [pdfWithStorage, docxWithStorage] = await serializeAttachmentsWithStorage(
              [pdfFile, docxFile],
              uid,
              `ld-${Date.now()}`
            );
            pdfSerialized = pdfWithStorage;
            docxSerialized = docxWithStorage;
          } catch {
            // Fall back to local serialization
            const [ps, ds] = await Promise.all([
              serializeFile(pdfFile),
              serializeFile(docxFile),
            ]);
            pdfSerialized = ps;
            docxSerialized = ds;
          }
        } else if (pdfFile && docxFile) {
          const [ps, ds] = await Promise.all([
            serializeFile(pdfFile),
            serializeFile(docxFile),
          ]);
          pdfSerialized = ps;
          docxSerialized = ds;
        }
      } catch (docErr) {
        console.error('[LegalDocCreator] Document file generation failed:', docErr);
      }

      // Store workflow output for chaining (use renderedText so downstream
      // workflows — e.g. translate — don't re-ingest the raw pipe artifacts)
      const createdAtForStore = new Date();
      storeWorkflowOutput({
        content: renderedText,
        title: docTitle,
        timestamp: createdAtForStore,
        source: 'formatted',
      });

      // Set shared context for translate workflow
      const translatableData = {
        content: renderedText,
        title: docTitle,
        timestamp: createdAtForStore,
        source: 'formatted' as const,
      };
      ctx.sharedContext.setLastTranslatableContent(translatableData);
      ctx.sharedContext.lastTranslatableContentRef.current = translatableData;
      ctx.sharedContext.pushContentHistory?.(translatableData);

      // Action buttons
      //
      // Save-as-Reusable-Template (Layer D, Miles 2026-05-08) is only shown
      // for the freeform general-document path. For the 8 legacy templates
      // and for their user-saved overrides we keep the existing button set
      // so we don't clutter the canonical flows.
      const saveAsTemplateButton = useFreeformDrafter
        ? [
            {
              label: language === 'es'
                ? 'Guardar como plantilla reutilizable'
                : 'Save as reusable template',
              id: BUTTON_IDS.saveAsTemplate,
              background: PREST0_SECONDARY_CTA_BG,
            },
          ]
        : [];
      const actionButtonsHtml = generateWorkflowActionButtonsHtml(
        [
          {
            label: 'translate',
            id: TRANSLATE_BUTTON_IDS.translate,
            background: PREST0_PRIMARY_CTA_BG,
          },
          ...saveAsTemplateButton,
        ],
        { theme: 'blue' }
      );

      // Build attachments arrays
      const attachments: File[] = [];
      const attachmentsSerialized: SerializedAttachment[] = [];
      if (pdfFile) attachments.push(pdfFile);
      if (docxFile) attachments.push(docxFile);
      if (pdfSerialized) attachmentsSerialized.push(pdfSerialized);
      if (docxSerialized) attachmentsSerialized.push(docxSerialized);

      stream.finalize({
        text: renderedText,
        html: formatResearchToHtml(renderedText),
        sourceTag: 'legal_doc_result',
        attachments: attachments.length > 0 ? attachments : undefined,
        attachmentsSerialized:
          attachmentsSerialized.length > 0 ? attachmentsSerialized : undefined,
        tokenUsage: tokenUsage
          ? { ...tokenUsage, summary: 'legal document generation' }
          : undefined,
        workflowOutput: {
          type: 'formatted_content',
          content: renderedText,
          title: docTitle,
          timestamp: createdAtForStore,
          sourceWorkflow: 'legal-doc-creator',
        },
        workflowActionButtonsHtml: actionButtonsHtml,
      });
      streamFinalized = true;

      // Record transaction for cost tracking
      if (tokenUsage) {
        ctx.recordTransaction({
          tokenUsage,
          source: 'agenticWorkflows',
          modelOverride: buildModelDisplay(
            tokenUsage,
            'Legal Doc Generation',
            CLAUDE_FORMATTER_DISPLAY
          ),
          summary: `${template.name} generation`,
        });
      }

      ctx.setState({ isGenerating: false });
      return { status: 'completed' };
    } catch (error) {
      ctx.setState({ isGenerating: false });
      const errMsg = error instanceof Error ? error.message : 'Unknown error occurred';

      if (!streamFinalized) {
        stream.finalize({
          payload: {
            kind: 'legal_doc.error',
            toolId: 'legal-doc-creator',
            severity: 'error',
            data: { errorMessage: errMsg, phase: 'generation' },
            summary: `legal document generation failed: ${errMsg}`,
          },
          sourceTag: 'legal_doc_error',
        });
        streamFinalized = true;
      }

      return {
        status: 'failed',
        error: new Error(errMsg),
        userFacingErrorShown: true,
      };
    } finally {
      ctx.setState({ isGenerating: false });
      if (!streamFinalized) {
        stream.finalize({
          payload: {
            kind: 'legal_doc.error',
            toolId: 'legal-doc-creator',
            severity: 'error',
            data: { errorMessage: 'generation encountered an unexpected issue', phase: 'emergency_fallback' },
            summary: 'legal document: emergency fallback fired (no specific cause captured)',
          },
          sourceTag: 'legal_doc_emergency',
        });
      }
    }
  },

  cleanup: async (ctx: StepContext<LegalDocCreatorState>): Promise<void> => {
    ctx.setState({ isGenerating: false });
  },
};

// ============================================================================
// Save-as-Reusable-Template Step (Layer D, Miles 2026-05-08)
//
// Multi-turn step: walks the user through title / description / scope /
// attorney-review flag, then persists the template to Firestore in the
// chosen scope. For 'global-candidate' scope, runs the auto-approve
// heuristic and either publishes with status:'active' or files a
// pending-review record with a best-effort Slack webhook ping.
//
// Bilingual pair: on save, we best-effort auto-generate the opposite-
// language drafter prompt so future generations render in whichever
// language is active at the time. If the auto-translation fails, we
// persist the single-language prompt so nothing is lost.
// ============================================================================

async function autoTranslateDrafterPrompt(
  sourcePrompt: string,
  sourceLang: 'en' | 'es'
): Promise<string> {
  // Lightweight translator using the same Anthropic proxy the rest of the
  // tool uses. Best-effort: on any error, return the source text unchanged
  // so we still have a usable prompt for the opposite language slot.
  try {
    const proxyUrl = getAnthropicProxyUrl();
    const targetLang = sourceLang === 'en' ? 'Spanish' : 'English';
    // D14 slice 2: route NAME only ('translation'); the server stamps the
    // SSOT ladder + effort. (Slice 7, 2026-09-15: the deploy-skew fallback
    // retired — best-effort by design, a failure returns the source text.)
    const translateBaseBody = {
      max_tokens: 4096,
      stream: false,
      system: `You translate legal-drafter system prompts between English and Spanish while preserving their structure, instructions, and placeholders verbatim (do not translate bracketed placeholders like [CITATION NEEDED]). Output ONLY the translated prompt, no preamble.`,
      messages: [
        { role: 'user', content: `Translate the following drafter system prompt to ${targetLang}. Preserve all markdown headings, bullet structure, and bracketed placeholders.\n\n---\n${sourcePrompt}` },
      ],
    };
    const response = await fetch(proxyUrl, {
      method: 'POST',
      // Who is calling — the signed-in user's ID token (security slice 6, ../../proxyAuth).
      headers: { 'Content-Type': 'application/json', 'X-Prest0n-Task': 'translation', ...(await proxyAuthHeaders()) },
      body: JSON.stringify({ mode: 'task', route: 'translation', ...translateBaseBody }),
    }).catch(() => null);
    if (!response || !response.ok) return sourcePrompt;
    const data = (await response.json()) as { content?: Array<{ text?: string }> };
    const translated = Array.isArray(data.content)
      ? data.content.map((c) => c.text || '').join('')
      : '';
    return translated.trim() || sourcePrompt;
  } catch {
    return sourcePrompt;
  }
}

const saveAsTemplateStep = {
  id: 'save-as-template',
  name: 'Save as Reusable Template',
  activeDisplayName: 'saving reusable template',

  execute: async (
    ctx: StepContext<LegalDocCreatorState>,
    _rawInput: unknown
  ): Promise<StepResult> => {
    const language = ctx.state.documentLanguage || 'en';
    const generatedContent = ctx.state.generatedContent;

    if (!generatedContent) {
      ctx.messaging.showWarning(
        language === 'es' ? 'Nada que guardar' : 'Nothing to save',
        language === 'es'
          ? 'No se encontró un documento generado en este hilo.'
          : 'No generated document found in this thread.'
      );
      return { status: 'completed', nextStep: null };
    }

    // Allow the user to cancel mid-flow at any stage.
    if (ctx.userInput !== undefined && isCancelCommand(ctx.userInput)) {
      ctx.setState({ savePending: undefined });
      ctx.messaging.showSpecialMessage({
        header: { title: 'cancelled', icon: '✕' },
        sections: [{
          type: 'text',
          content: language === 'es'
            ? 'Flujo de guardado cancelado. El documento generado sigue disponible arriba.'
            : 'Save flow cancelled. Your generated document remains available above.',
        }],
        theme: 'custom',
        customColors: LEGAL_DOC_THEME,
      });
      return { status: 'completed', nextStep: null };
    }

    const pending = ctx.state.savePending || { stage: 'awaiting-title' as const };

    // ---- Stage 1: title ----
    if (pending.stage === 'awaiting-title') {
      if (ctx.userInput === undefined) {
        const defaultTitle = ctx.state.generatedTitle || (language === 'es' ? 'Plantilla sin título' : 'Untitled Template');
        ctx.setState({ savePending: { stage: 'awaiting-title' } });
        ctx.messaging.showSpecialMessage({
          header: {
            title: language === 'es' ? 'guardar como plantilla reutilizable' : 'save as reusable template',
            icon: '💾',
          },
          sections: [
            {
              type: 'text',
              content: language === 'es'
                ? `Elige un título para esta plantilla. Título sugerido (presiona enter para aceptar): **${defaultTitle}**`
                : `Choose a title for this reusable template. Suggested title (press enter to accept): **${defaultTitle}**`,
            },
            {
              type: 'text',
              content: language === 'es'
                ? 'Escribe *cancelar* en cualquier momento para salir.'
                : 'Type *cancel* at any time to exit.',
            },
          ],
          theme: 'custom',
          customColors: LEGAL_DOC_THEME,
          helperText: language === 'es' ? 'Escribe el título o presiona enter' : 'Type the title or press enter',
        });
        return { status: 'awaiting-input' };
      }

      const titleInput = (ctx.userInput || '').trim();
      const resolvedTitle =
        titleInput.length > 0
          ? titleInput
          : ctx.state.generatedTitle || 'Untitled Template';
      ctx.setState({
        savePending: {
          ...pending,
          title: resolvedTitle,
          stage: 'awaiting-description',
        },
      });
      ctx.messaging.showSpecialMessage({
        header: {
          title: language === 'es' ? 'descripción breve' : 'short description',
          icon: '📝',
        },
        sections: [{
          type: 'text',
          content: language === 'es'
            ? 'En una oración, ¿para qué sirve esta plantilla? (o escribe *skip* para omitir)'
            : 'In one sentence, what is this template for? (or type *skip* to leave blank)',
        }],
        theme: 'custom',
        customColors: LEGAL_DOC_THEME,
        helperText: language === 'es' ? 'Escribe una descripción breve' : 'Type a short description',
      });
      return { status: 'awaiting-input' };
    }

    // ---- Stage 2: description ----
    if (pending.stage === 'awaiting-description') {
      const descInput = (ctx.userInput || '').trim();
      const resolvedDesc = /^skip$/i.test(descInput) || descInput.length === 0 ? '' : descInput;
      ctx.setState({
        savePending: {
          ...pending,
          description: resolvedDesc,
          stage: 'awaiting-scope',
        },
      });
      ctx.messaging.showSpecialMessage({
        header: {
          title: language === 'es' ? 'alcance de la plantilla' : 'template scope',
          icon: '🌐',
        },
        sections: [
          {
            type: 'text',
            content: language === 'es'
              ? 'Elige quién puede usar esta plantilla:'
              : 'Choose who can use this template:',
          },
          {
            type: 'list',
            content: language === 'es'
              ? [
                  '**Solo yo (User)** — sólo disponible en tu cuenta.',
                  '**Mi firma (Firm)** — compartida con otros usuarios de tu firma.',
                  '**Candidata global (Global-candidate)** — propuesta para publicación en toda la plataforma (requiere aprobación si no es genérica).',
                ]
              : [
                  '**Just me (User)** — only available in your account.',
                  '**My firm (Firm)** — shared with other users of your firm.',
                  '**Global candidate** — proposed for platform-wide publication (requires review unless it\'s generic).',
                ],
          },
          {
            type: 'text',
            content: language === 'es'
              ? 'Responde con *user*, *firm*, o *global*.'
              : 'Reply with *user*, *firm*, or *global*.',
          },
        ],
        theme: 'custom',
        customColors: LEGAL_DOC_THEME,
        helperText: 'user / firm / global',
      });
      return { status: 'awaiting-input' };
    }

    // ---- Stage 3: scope ----
    if (pending.stage === 'awaiting-scope') {
      const scopeInput = (ctx.userInput || '').trim().toLowerCase();
      let scope: 'user' | 'tenant' | 'global-candidate';
      if (/^(user|only me|just me|solo yo|usuario)\b/.test(scopeInput)) scope = 'user';
      else if (/^(firm|tenant|my firm|mi firma|firma)\b/.test(scopeInput)) scope = 'tenant';
      else if (/^(global|global[- ]candidate|platform|candidata|global candidate)\b/.test(scopeInput)) scope = 'global-candidate';
      else {
        ctx.messaging.showWarning(
          language === 'es' ? 'Alcance no reconocido' : 'Scope not recognized',
          language === 'es'
            ? 'Por favor responde con *user*, *firm*, o *global*.'
            : 'Please reply with *user*, *firm*, or *global*.'
        );
        return { status: 'awaiting-input' };
      }
      ctx.setState({
        savePending: {
          ...pending,
          scope,
          stage: 'awaiting-attorney-review',
        },
      });
      ctx.messaging.showSpecialMessage({
        header: {
          title: language === 'es' ? '¿bandera de revisión de abogado?' : 'attorney-review flag?',
          icon: '⚖',
        },
        sections: [
          {
            type: 'text',
            content: language === 'es'
              ? '¿Mostrar una bandera "[REVISIÓN DE ABOGADO REQUERIDA]" al inicio de cada documento generado con esta plantilla? (predeterminado: **no**)'
              : 'Show an "[ATTORNEY REVIEW REQUIRED]" banner at the top of each document generated from this template? (default: **no**)',
          },
          {
            type: 'text',
            content: language === 'es' ? 'Responde *yes* / *no* (enter para no).' : 'Reply *yes* / *no* (enter for no).',
          },
        ],
        theme: 'custom',
        customColors: LEGAL_DOC_THEME,
        helperText: 'yes / no',
      });
      return { status: 'awaiting-input' };
    }

    // ---- Stage 4: attorney-review flag + actually save ----
    if (pending.stage === 'awaiting-attorney-review') {
      const arInput = (ctx.userInput || '').trim().toLowerCase();
      const requiresAttorneyReview = /^(yes|y|s[ií]|si)\b/.test(arInput);

      const scope = pending.scope || 'user';
      const title = pending.title || ctx.state.generatedTitle || 'Untitled Template';
      const description = pending.description || '';

      // Build the drafter prompt pair from the generation that produced this
      // content. We use the default freeform drafter prompt as the baseline
      // (which is what actually produced the draft); the opposite-language
      // version is auto-translated best-effort so future users in either
      // language get a sensible prompt.
      const authoredPrompt = defaultFreeformDrafterSystemPrompt(language, requiresAttorneyReview);
      let otherPrompt: string;
      try {
        otherPrompt = await autoTranslateDrafterPrompt(authoredPrompt, language);
      } catch {
        otherPrompt = authoredPrompt;
      }
      const freeformDrafterPromptEn = language === 'en' ? authoredPrompt : otherPrompt;
      const freeformDrafterPromptEs = language === 'es' ? authoredPrompt : otherPrompt;

      // Auto-suggest the field schema from what the user filled in on the
      // most recent generation. Any field with a non-empty value is carried
      // forward; the required-ness defaults to the original schema.
      const originalTemplate = ctx.state.selectedTemplate
        ? getTemplateById(ctx.state.selectedTemplate)
        : undefined;
      const carriedFields = ctx.state.collectedFields || {};
      const suggestedFields = (originalTemplate?.fields || []).filter((f) => !!carriedFields[f.id]).map((f) => ({ ...f }));
      // Guarantee at least documentPurpose is always present so saved
      // templates remain usable in the freeform drafter path.
      if (!suggestedFields.some((f) => f.id === 'documentPurpose')) {
        suggestedFields.unshift({
          id: 'documentPurpose',
          label: 'What document do you need? (describe the purpose)',
          labelEs: '¿Qué documento necesitas? (describe el propósito)',
          type: 'textarea',
          required: true,
          group: 'purpose',
        });
      }

      const saveInput: SaveTemplateInput = {
        title,
        description,
        scope,
        fields: suggestedFields,
        requiresAttorneyReview,
        generatedBody: generatedContent,
        authoredLanguage: language,
        freeformDrafterPromptEn,
        freeformDrafterPromptEs,
      };

      const pending2 = ctx.messaging.showPending(
        language === 'es' ? 'guardando plantilla' : 'saving template',
        'legal_doc_saving_template'
      );

      try {
        const result = await saveReusableTemplate(saveInput);
        ctx.setState({ savePending: undefined });

        const body = language === 'es'
          ? `Plantilla guardada. Alcance: **${result.scope}**. Estado: **${result.status}**.${result.autoApproved ? ' (aprobada automáticamente)' : ''}${result.reason ? ` Motivo: ${result.reason}` : ''}`
          : `Template saved. Scope: **${result.scope}**. Status: **${result.status}**.${result.autoApproved ? ' (auto-approved)' : ''}${result.reason ? ` Reason: ${result.reason}` : ''}`;

        pending2.finalize({
          text: body,
          html: `<div style="padding:12px;font-family:sans-serif;">${body}</div>`,
          sourceTag: 'legal_doc_template_saved',
        });
        return { status: 'completed', nextStep: null };
      } catch (err) {
        ctx.setState({ savePending: undefined });
        const msg = err instanceof Error ? err.message : 'Unknown error';
        pending2.finalize({
          text: (language === 'es' ? 'No se pudo guardar la plantilla: ' : 'Could not save template: ') + msg,
          html: `<div style="color:#dc2626;padding:12px;font-family:sans-serif;">${language === 'es' ? 'No se pudo guardar la plantilla' : 'Could not save template'}: ${msg}</div>`,
          sourceTag: 'legal_doc_template_save_error',
        });
        return { status: 'completed', nextStep: null };
      }
    }

    // Safety: unknown stage — reset.
    ctx.setState({ savePending: undefined });
    return { status: 'completed', nextStep: null };
  },
};

// ============================================================================
// Tool Definition
// ============================================================================

// Validate that all templates are listed (development check)
const _templateCheck = LEGAL_TEMPLATES.length > 0;
void _templateCheck;

export const legalDocCreatorTool: GlobalToolDefinition<LegalDocCreatorState> = {
  id: 'legal-doc-creator',
  name: 'Legal Document Creator',
  description:
    'Create professional legal documents from templates with guided input collection',
  triggerPattern: matchLegalDocTrigger,
  createInitialState: (): LegalDocCreatorState => ({
    selectedTemplate: null,
    collectedFields: {},
    documentLanguage: 'en',
    isGenerating: false,
    originalPacketFileCount: 0,
  }),
  steps: [selectTypeStep, gatherDetailsStep, generateStep, saveAsTemplateStep],
  isAuthorized: isAgenticWorkflowsAuthorized,
  theme: LEGAL_DOC_THEME,
  buttonIds: BUTTON_IDS,
  canTriggerFromButton: true,
};
